//基础结构
<template>
  <div class="home-container">
    <!-- 用户积分模块 -->
    <div class="module-card points-module" v-if="!isAdmin">
      <div class="points-content">
        <div class="points-info">
          <div class="points-value">
            <span class="points-number">{{ userInfo ? userInfo.points : '0' }}</span>
            <span class="points-label">积分</span>
          </div>
        </div>
        <button class="exchange-btn" @click="showExchangeModal">
          兑换积分
        </button>
      </div>
      <p v-if="loadingUserInfo" class="loading-text">加载中...</p>
      <p v-if="userInfoError" class="error-text">{{ userInfoError }}</p>
    </div>

    <!-- 兑换积分模态框 -->
    <div class="modal-overlay" v-if="showModal" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>兑换积分</h3>
          <button class="close-btn" @click="closeModal">×</button>
        </div>
        <div class="modal-body">
          <div class="input-group">
            <input 
              type="text" 
              v-model="cardKey" 
              placeholder="请输入卡密"
              class="card-input"
            />
          </div>
          <p class="tip-text">请联系管理员获取卡密</p>
          <button class="submit-btn" @click="handleSubmitExchange">
            立即兑换
          </button>
        </div>
      </div>
    </div>

    <!-- 预设动作模块 -->
    <div class="module-card action-module" v-if="!isRegularUser">
      <h3 class="module-title">动作管理</h3>
      <p class="module-description">保存并应用编辑操作序列，可用于不同图像</p>
      <div class="preset-action-wrapper">
        <select 
          v-model.number="selectedPreset" 
          class="preset-select"
          :disabled="isExecutingPreset || isRecording || isPlayingAction"
        >
          <option value="">选择预设动作</option>
          <option 
            v-for="preset in presetActions" 
            :key="preset.id" 
            :value="preset.id"
          >
            {{ preset.description }}
          </option>
        </select>
        <button 
          v-if="selectedPreset" 
          @click="executePreset" 
          class="execute-btn"
          :disabled="isExecutingPreset || layers.length < 2 || isRecording || isPlayingAction"
        >
          {{ isExecutingPreset ? '执行中...' : '执行动作' }}
        </button>
        <button 
          @click="startRecording" 
          class="record-btn"
          :class="{ 'recording': isRecording }"
          :disabled="isPlayingAction"
          v-if="!isRegularUser"
        >
          {{ isRecording ? '结束录制' : '录制动作' }}
        </button>
        <button 
          @click="toggleActionManager" 
          class="manage-btn"
          :disabled="isRecording || isPlayingAction"
          v-if="!isRegularUser"
        >
          管理动作
        </button>
      </div>

      <!-- 动作管理器面板 -->
      <div v-if="showActionManager" class="action-manager">
        <div class="action-list">
          <div v-if="loadedActions.length === 0" class="no-actions">
            暂无已加载的动作，请点击下方按钮加载
          </div>
          <div v-else v-for="(action, index) in loadedActions" :key="index" class="action-item">
            <div class="action-info">
              <span class="action-name">{{ action.name }}</span>
              <span class="action-date">{{ new Date(action.createdAt).toLocaleString() }}</span>
            </div>
            <div class="action-buttons">
              <button 
                @click="playAction(index)" 
                class="play-btn"
                :disabled="isRecording || isPlayingAction"
              >
                {{ isPlayingAction && currentActionIndex === index ? '执行中...' : '执行' }}
              </button>
              <button 
                @click="openEditModal(index)" 
                class="edit-btn"
                :disabled="isPlayingAction"
              >
                编辑
              </button>
              <button 
                @click="deleteAction(index)" 
                class="delete-btn"
                :disabled="isPlayingAction"
              >
                删除
              </button>
            </div>
          </div>
        </div>
        <div class="action-manager-footer">
          <button 
            @click="refreshActionList" 
            class="refresh-btn"
            :disabled="isPlayingAction"
          >
            <span class="refresh-icon">↻</span> 刷新动作列表
          </button>
        </div>
      </div>
    </div>
    
    <!-- 普通用户预设动作模块 -->
    <div class="module-card user-action-module" v-if="isRegularUser">
      <h3 class="module-title">可用动作</h3>
      <p class="module-description">选择并使用预设的图像处理动作</p>
      
      <div class="user-actions-list">
        <div v-if="loadingActions" class="loading-actions">
          <p>加载中...</p>
        </div>
        <div v-else-if="actionError" class="error-message">
          <p>{{ actionError }}</p>
        </div>
        <div v-else-if="loadedActions.length === 0" class="no-actions">
          暂无可用动作
        </div>
        <div v-else v-for="action in loadedActions" :key="action.id" 
          class="user-action-item" :class="{ 'disabled': userInfo.points < action.required_credits }">
          <div class="action-main-info">
            <div class="action-title">
              <h4>{{ action.name }}</h4>
              <span class="action-status" :class="{ 'available': userInfo.points >= action.required_credits }">
                {{ userInfo.points >= action.required_credits ? '可用' : '积分不足' }}
              </span>
            </div>
            <div class="action-description" v-if="action.description">
              {{ action.description }}
            </div>
            <div class="action-requirements">
              <span class="requirement-item">
                <i class="requirement-icon">🖼️</i>
                所需图片: {{ action.required_images }}张
              </span>
              <span class="requirement-item">
                <i class="requirement-icon">💎</i>
                所需积分: {{ action.required_credits }}
              </span>
            </div>
          </div>
          
          <div class="action-buttons">
            <button 
              class="preview-btn"
              @click="previewAction(action.id)"
              :disabled="isPlayingAction"
            >
              查看示例
            </button>
            <button 
              class="execute-action-btn"
              @click="executeUserAction(action)"
              :disabled="isPlayingAction || userInfo.points < action.required_credits || layers.length < action.required_images"
            >
              {{ isPlayingAction && currentActionIndex === action.id ? '执行中...' : '执行' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 上传图片模块 -->
    <div class="module-card upload-module">
      <h3 class="module-title">上传图片</h3>
      <div class="upload-section">
        <input 
          type="file" 
          accept="image/*" 
          ref="fileInput" 
          @change="handleFileUpload" 
          class="file-input"
        />
        <button class="upload-btn" @click="triggerFileInput">
          <span class="upload-icon">+</span> {{ layers.length === 0 ? '上传图片' : '添加图层' }}
        </button>
        
        <div v-if="imageLoaded" class="image-info">
          <p>
            <span class="info-label">显示尺寸:</span> 
            <span class="info-value">{{ displayCanvasSize }} × {{ displayCanvasSize }}像素</span>
          </p>
          <p>
            <span class="info-label">导出尺寸:</span> 
            <span class="info-value">{{ highResCanvasSize }} × {{ highResCanvasSize }}像素</span>
          </p>
          <p v-if="layers.length > 0">
            <span class="info-label">当前图层:</span> 
            <span class="info-value">{{ activeLayerIndex + 1 }}/{{ layers.length }}</span>
          </p>
        </div>
      </div>
    </div>
    
    <!-- 缩放控制模块 -->
    <div class="module-card zoom-module" v-if="imageLoaded">
      <h3 class="module-title">缩放控制</h3>
      <div class="zoom-controls">
        <div class="zoom-slider">
          <input 
            type="range" 
            min="10" 
            max="200" 
            v-model="zoomLevel" 
            @input="applyZoom" 
          />
          <span>{{ zoomLevel }}%</span>
        </div>
        <div class="zoom-buttons">
          <button @click="resetZoom" class="zoom-btn">重置缩放</button>
          <button @click="zoomIn" class="zoom-btn">放大</button>
          <button @click="zoomOut" class="zoom-btn">缩小</button>
        </div>
      </div>
    </div>
    
    <!-- 画布模块 -->
    <div class="module-card canvas-module">
      <h3 class="module-title">画布</h3>
      <div class="canvas-wrapper">
        <div class="canvas-container">
          <canvas 
            ref="canvas" 
            class="main-canvas"
            @mousedown="startDraw"
            @touchstart="handleTouchStart"
            @mousemove="drawing"
            @touchmove="handleTouchMove"
            @mouseup="endDraw"
            @touchend="endDraw"
            @mouseleave="endDraw"
          ></canvas>
          <!-- 暂时隐藏的临时画布，用于处理 -->
          <canvas ref="tempCanvas" style="display: none;"></canvas>
        </div>
      </div>
    </div>

    <!-- 图层管理模块 -->
    <div class="module-card layers-module" v-if="layers.length > 0">
      <h3 class="module-title">图层管理</h3>
      <div class="layers-list">
        <div 
          v-for="(layer, index) in layers" 
          :key="layer.id"
          :class="['layer-item', activeLayerIndex === index ? 'active-layer' : '']"
          @click="setActiveLayer(index)"
        >
          <div class="layer-preview">
            <img :src="layer.thumbnail" alt="图层预览" class="layer-thumbnail" />
          </div>
          <div class="layer-info">
            <div class="layer-name">{{ layer.name }}</div>
            <div class="layer-controls">
              <input 
                type="range" 
                min="0" 
                max="100" 
                v-model="layer.opacity" 
                @input="updateLayerOpacity(index)"
              />
              <span class="layer-opacity">{{ layer.opacity }}%</span>
            </div>
          </div>
          <div class="layer-actions">
            <button @click.stop="toggleLayerVisibility(index)" class="visibility-btn">
              <img :src="layer.visible ? icons.look : icons.unlook" alt="visibility" class="action-icon"/>
            </button>
            <button @click.stop="moveLayerUp(index)" class="order-btn" :disabled="index === 0" title="上移图层">
              <span class="order-icon">↑</span>
            </button>
            <button @click.stop="moveLayerDown(index)" class="order-btn" :disabled="index === layers.length - 1" title="下移图层">
              <span class="order-icon">↓</span>
            </button>
            <button @click.stop="deleteLayer(index)" class="delete-btn">
              <img :src="icons.deleate" alt="delete" class="action-icon"/>
            </button>
          </div>
        </div>
      </div>
      <div class="layer-add-section">
        <button @click="triggerFileInput" class="add-layer-btn">
          <span class="plus-icon">+</span> 添加图层
        </button>
      </div>
    </div>
    
    <!-- 编辑按钮模块 -->
    <div class="module-card edit-actions-module">
      <h3 class="module-title">编辑操作</h3>
      <div class="canvas-actions">
        <button @click="undoLastOperation" class="action-btn" :disabled="canvasHistory.length === 0">
          <span class="icon">↩</span> 撤销
        </button>
        <button @click="resetCanvas" class="action-btn" :disabled="!imageLoaded">
          <span class="icon">⟲</span> 重置
        </button>
        <button @click="downloadImage" class="action-btn" :disabled="!imageLoaded">
          <span class="icon">↓</span> 下载
        </button>
        <button @click="goToWebVersion" class="action-btn web-version-btn">
          <span class="icon">🌐</span> Web版
        </button>
      </div>

      <!-- 添加图层操作按钮 -->
      <div class="layer-operations" v-if="activeLayerIndex >= 0">
        <button @click="duplicateLayer(activeLayerIndex)" class="action-btn">
          <span class="icon">⎘</span> 复制
        </button>
        <button @click="renameLayer" class="action-btn">
          <span class="icon">✎</span> 重命名
        </button>
        <button @click="toggleLayerMoveControls" class="action-btn" :class="{'active-tool': showLayerMoveControls}">
          <span class="icon">↕</span> 移动
        </button>
        <button @click="mergeDown" class="action-btn" :disabled="activeLayerIndex >= layers.length - 1">
          <span class="icon">⇓</span> 向下合并
        </button>
        <button @click="showMergeOptions" class="action-btn" :disabled="layers.length < 2">
          <span class="icon">⋈</span> 合并可见
        </button>
      </div>
      
      <!-- 图层移动控制 -->
      <div v-if="showLayerMoveControls && activeLayerIndex >= 0" class="layer-move-controls">
        <div class="move-controls-grid">
          <button class="move-btn up-btn" @click="moveLayerPosition(0, -1)" title="上移">
            <span class="direction-icon">↑</span>
          </button>
          <button class="move-btn left-btn" @click="moveLayerPosition(-1, 0)" title="左移">
            <span class="direction-icon">←</span>
          </button>
          <div class="move-center">
            <span class="pixel-info">{{ layers[activeLayerIndex].offsetX || 0 }}×{{ layers[activeLayerIndex].offsetY || 0 }}</span>
          </div>
          <button class="move-btn right-btn" @click="moveLayerPosition(1, 0)" title="右移">
            <span class="direction-icon">→</span>
          </button>
          <button class="move-btn down-btn" @click="moveLayerPosition(0, 1)" title="下移">
            <span class="direction-icon">↓</span>
          </button>
        </div>
        <div class="move-controls-actions">
          <button class="reset-position-btn" @click="resetLayerPosition" title="重置位置">
            重置位置
          </button>
        </div>
      </div>
    </div>
    
    <!-- 混合模式模块 -->
    <div class="module-card blend-module" v-if="imageLoaded">
      <h3 class="module-title">混合模式</h3>
      <div class="blend-mode-selector">
        <select v-model="blendMode" @change="updateBlendMode" class="blend-select">
          <option value="normal">正常</option>
          <option value="multiply">正片叠底</option>
          <option value="screen">滤色</option>
          <option value="overlay">叠加</option>
          <option value="darken">变暗</option>
          <option value="lighten">变亮</option>
          <option value="color-dodge">颜色减淡</option>
          <option value="color-burn">颜色加深</option>
          <option value="hard-light">强光</option>
          <option value="soft-light">柔光</option>
          <option value="difference">差值</option>
          <option value="exclusion">排除</option>
        </select>
      </div>
    </div>
    
    <!-- 滤镜效果模块 -->
    <div class="module-card filter-module" v-if="imageLoaded">
      <h3 class="module-title">滤镜效果</h3>
      <div class="filter-controls">
        <div class="filter-item">
          <label>高斯模糊</label>
          <input 
            type="range" 
            min="0" 
            max="20" 
            v-model="activeFilters.blur" 
            @input="updateFilters" 
          />
          <span>{{ activeFilters.blur }}px</span>
        </div>
        
        <div class="filter-item">
          <label>亮度</label>
          <input 
            type="range" 
            min="0" 
            max="200" 
            v-model="activeFilters.brightness" 
            @input="updateFilters" 
          />
          <span>{{ activeFilters.brightness }}%</span>
        </div>
        
        <div class="filter-item">
          <label>对比度</label>
          <input 
            type="range" 
            min="0" 
            max="200" 
            v-model="activeFilters.contrast" 
            @input="updateFilters" 
          />
          <span>{{ activeFilters.contrast }}%</span>
        </div>
        
        <div class="filter-item">
          <label>饱和度</label>
          <input 
            type="range" 
            min="0" 
            max="200" 
            v-model="activeFilters.saturation" 
            @input="updateFilters" 
          />
          <span>{{ activeFilters.saturation }}%</span>
        </div>
      </div>
    </div>
    
    <!-- 绘图工具模块 -->
    <div class="module-card drawing-module" v-if="imageLoaded">
      <h3 class="module-title">绘图工具</h3>
      <div class="drawing-tools">
        <div class="tool-buttons">
          <!-- 暂时注释掉画笔工具
          <button 
            :class="['tool-btn', currentTool.type === 'brush' ? 'active' : '']"
            @click="selectTool('brush')"
          >
            画笔
          </button>
          -->
          <button 
            :class="['tool-btn', currentTool.active && currentTool.type === 'eraser' ? 'active' : '']"
            @click="toggleEraser"
          >
            {{ currentTool.active ? '使用中' : '选择' }} 橡皮擦
          </button>
        </div>
        
        <div v-if="currentTool.active && (currentTool.type === 'brush' || currentTool.type === 'eraser')" class="tool-options">
          <label>
            大小:
            <input 
              type="range" 
              min="1" 
              max="50" 
              v-model="currentTool.size" 
              @input="updateToolSize" 
            />
            {{ currentTool.size }}px
          </label>
          
          <label v-if="currentTool.type === 'eraser'">
            硬度:
            <input 
              type="range" 
              min="0" 
              max="100" 
              v-model="currentTool.hardness" 
              @input="updateToolHardness" 
            />
            {{ currentTool.hardness }}%
          </label>
        </div>
      </div>
    </div>

    <!-- 图层工具模块 -->
    <div class="module-card layer-tools-module" v-if="imageLoaded">
      <h3 class="module-title">图层工具</h3>
      <div class="layer-tools">
        <!-- 工具按钮区域 -->
        <div class="layer-tool-buttons">
          <!-- 反相工具 -->
          <button 
            class="layer-tool-btn"
            @click="invertLayer"
            :disabled="activeLayerIndex < 0"
          >
            反相
          </button>
          
          <!-- 描边工具 -->
          <button 
            class="layer-tool-btn"
            @click="openStrokeOptions"
            :disabled="activeLayerIndex < 0"
          >
            描边
          </button>

          <!-- 全选工具 -->
          <button 
            class="layer-tool-btn"
            :class="{'active-tool': hasSelection}"
            @click="selectAll"
            :disabled="activeLayerIndex < 0"
          >
            {{ hasSelection ? '取消全选' : '全选' }}
          </button>

          <!-- 选区调整工具 -->
          <button 
            class="layer-tool-btn"
            @click="openSelectionOptions"
            :disabled="activeLayerIndex < 0 || !hasSelection"
          >
            调整选区
          </button>

          <!-- 删除选区工具 -->
          <button 
            class="layer-tool-btn"
            @click="deleteSelection"
            :disabled="activeLayerIndex < 0 || !hasSelection"
          >
            删除选区
          </button>

          <!-- 复制选区工具 -->
          <button 
            class="layer-tool-btn"
            @click="copySelection"
            :disabled="activeLayerIndex < 0 || !hasSelection"
          >
            复制选区
          </button>

          <!-- 放大图层工具 -->
          <button 
            class="layer-tool-btn"
            @click="openScaleOptions"
            :disabled="activeLayerIndex < 0"
          >
            放大图层
          </button>

          <!-- 填充工具 -->
          <button 
            class="layer-tool-btn"
            @click="openFillOptions"
            :disabled="activeLayerIndex < 0 || !hasSelection"
          >
            填充
          </button>
          
          <!-- 填充图层工具 -->
          <button 
            class="layer-tool-btn"
            @click="openFillLayerOptions"
            :disabled="activeLayerIndex < 0"
          >
            填充图层
          </button>
        </div>
        
        <!-- 描边选项面板 -->
        <div v-if="showStrokeOptions" class="stroke-options">
          <div class="stroke-option">
            <label>
              描边大小:
              <input 
                type="range" 
                min="1" 
                max="20" 
                v-model="strokeOptions.size" 
              />
              {{ strokeOptions.size }}px
            </label>
          </div>
          
          <div class="stroke-option">
            <label>
              渐变强度:
              <input 
                type="range" 
                min="0" 
                max="100" 
                v-model="strokeOptions.gradientIntensity" 
              />
              {{ strokeOptions.gradientIntensity }}%
            </label>
          </div>
          
          <div class="stroke-option">
            <label>
              描边颜色:
              <input 
                type="color" 
                v-model="strokeOptions.color" 
                class="color-picker"
              />
            </label>
          </div>
          
          <div class="stroke-actions">
            <button 
              class="apply-btn"
              @click="applyStroke"
            >
              应用
            </button>
            <button 
              class="cancel-btn"
              @click="showStrokeOptions = false"
            >
              取消
            </button>
          </div>
        </div>

        <!-- 填充选项面板 -->
        <div v-if="showFillOptions" class="fill-options">
          <div class="fill-option">
            <label>
              填充颜色:
              <input 
                type="color" 
                v-model="fillOptions.color" 
                class="color-picker"
              />
            </label>
          </div>
          
          <div class="fill-actions">
            <button 
              class="apply-btn"
              @click="applyFill"
            >
              应用
            </button>
            <button 
              class="cancel-btn"
              @click="showFillOptions = false"
            >
              取消
            </button>
          </div>
        </div>
        
        <!-- 填充图层选项面板 -->
        <div v-if="showFillLayerOptions" class="fill-layer-options">
          <div class="fill-layer-option">
            <label>
              填充类型:
              <select v-model="fillLayerOptions.type" class="fill-type-select">
                <option value="color">纯色填充</option>
                <option value="image">图片填充</option>
              </select>
            </label>
          </div>
          
          <!-- 纯色填充选项 -->
          <div v-if="fillLayerOptions.type === 'color'" class="fill-layer-option">
            <label>
              填充颜色:
              <input 
                type="color" 
                v-model="fillLayerOptions.color" 
                class="color-picker"
              />
            </label>
          </div>
          
          <!-- 图片填充选项 -->
          <div v-if="fillLayerOptions.type === 'image'" class="fill-layer-option">
            <label>
              选择图片:
              <input 
                type="file" 
                @change="handleFillImageUpload" 
                accept="image/*"
                class="image-upload"
              />
            </label>
            <div v-if="fillLayerOptions.previewUrl" class="fill-image-preview">
              <img :src="fillLayerOptions.previewUrl" alt="填充图片预览" />
            </div>
          </div>
          
          <div class="fill-layer-actions">
            <button 
              class="apply-btn"
              @click="applyFillLayer"
            >
              应用
            </button>
            <button 
              class="cancel-btn"
              @click="showFillLayerOptions = false"
            >
              取消
            </button>
          </div>
        </div>
        
        <!-- 选区调整选项面板 -->
        <div v-if="showSelectionOptions" class="selection-adjust-options">
          <div class="option-header">
            <h4 class="option-title">调整选区</h4>
            <button class="close-btn" @click="showSelectionOptions = false">×</button>
          </div>
          
          <div class="selection-option">
            <label>
              选区大小调整:
              <input 
                type="range" 
                min="-20" 
                max="20" 
                v-model.number="selectionOptions.sizeAdjust" 
                @input="previewSelectionAdjust"
              />
              {{ selectionOptions.sizeAdjust > 0 ? '+' + selectionOptions.sizeAdjust : selectionOptions.sizeAdjust }}px
            </label>
          </div>
          
          <div class="selection-option">
            <label>
              边缘平滑度:
              <input 
                type="range" 
                min="0" 
                max="5" 
                step="0.5" 
                v-model.number="selectionOptions.smoothness" 
                @input="previewSelectionAdjust"
              />
              {{ selectionOptions.smoothness }}
            </label>
          </div>
          
          <div class="selection-actions">
            <button 
              class="apply-btn"
              @click="applySelectionAdjust"
            >
              应用
            </button>
            <button 
              class="cancel-btn"
              @click="cancelSelectionAdjust"
            >
              取消
            </button>
          </div>
        </div>
        
        <!-- 放大选项面板 -->
        <div v-if="showScaleOptions" class="scale-options">
          <div class="scale-option">
            <label>
              放大比例:
              <input 
                type="number" 
                v-model.number="scaleOptions.scale"
                min="100"
                max="400"
                step="10"
                class="scale-input"
              />
              %
            </label>
          </div>
          <div class="scale-option">
            <label>
              <input 
                type="checkbox" 
                v-model="scaleOptions.maintainAspectRatio"
              />
              保持宽高比
            </label>
          </div>
          <div class="scale-actions">
            <button 
              class="apply-btn"
              @click="applyScale"
            >
              应用
            </button>
            <button 
              class="cancel-btn"
              @click="showScaleOptions = false"
            >
              取消
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 编辑动作模态框 -->
    <div class="modal-overlay" v-if="showEditModal" @click="closeEditModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>编辑动作积分</h3>
          <button class="close-btn" @click="closeEditModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>动作名称</label>
            <input 
              type="text" 
              v-model="editingAction.name"
              class="form-input"
              placeholder="请输入动作名称"
              readonly
            />
          </div>
          <div class="form-group">
            <label>所需图片数量</label>
            <input 
              type="number" 
              v-model.number="editingAction.required_images"
              class="form-input"
              min="1"
              placeholder="请输入所需图片数量"
            />
          </div>
          <div class="form-group">
            <label>所需积分</label>
            <input 
              type="number" 
              v-model.number="editingAction.required_credits"
              class="form-input"
              min="0"
              placeholder="请输入所需积分"
            />
          </div>
          <div class="form-group">
            <label>描述</label>
            <textarea 
              v-model="editingAction.description"
              class="form-textarea"
              placeholder="请输入动作描述"
            ></textarea>
          </div>
          <div class="form-group">
            <label>状态</label>
            <select v-model="editingAction.status" class="form-select">
              <option :value="1">启用</option>
              <option :value="0">禁用</option>
            </select>
          </div>
          <div class="form-actions">
            <button class="save-btn" @click="saveAction">保存</button>
            <button class="cancel-btn" @click="closeEditModal">取消</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 预览动作效果模态框 -->
    <div class="modal-overlay" v-if="previewModalVisible" @click="previewModalVisible = false">
      <div class="modal-content preview-modal" @click.stop>
        <div class="modal-header">
          <h3>{{ currentPreviewAction ? currentPreviewAction.name : '' }} - 效果预览</h3>
          <button class="close-btn" @click="previewModalVisible = false">×</button>
        </div>
        <div class="modal-body preview-body">
          <div class="preview-image-container">
            <img :src="previewImageUrl" alt="预览图" class="preview-image" v-if="previewImageUrl">
            <div class="no-preview" v-else>暂无预览图</div>
          </div>
          <div class="preview-info" v-if="currentPreviewAction">
            <div class="preview-requirements">
              <div class="requirement-item">
                <i class="requirement-icon">🖼️</i>
                所需图片: {{ currentPreviewAction.required_images }}张
              </div>
              <div class="requirement-item">
                <i class="requirement-icon">💎</i>
                所需积分: {{ currentPreviewAction.required_credits }}
              </div>
            </div>
            <p class="preview-description">{{ currentPreviewAction.description || '暂无描述' }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { presetActions, executePreset } from '@/presets';
import lookIcon from '@/assets/icon/look.svg';
import unlookIcon from '@/assets/icon/unlook.svg';
import deleateIcon from '@/assets/icon/deleate.svg';
import { useRouter } from 'vue-router';
import { api } from '../utils/axios';

export default {
  name: 'HomeView',
  setup() {
    const router = useRouter();
    return { router };
  },
  data() {
    return {
      // 添加录制相关的状态
      isRecording: false,
      recordedActions: [],
      currentRecordName: '',
      actionSnapshots: [], // 存储每个操作的完整画布状态快照
      
      // 移动端适配相关
      isMobileView: false,
      sidebarVisible: true,
      
      // 图片和画布相关
      originalImage: null,
      ctx: null,
      tempCtx: null,
      imageLoaded: false,
      imageWidth: 0,
      imageHeight: 0,
      
      // 图层系统
      layers: [],
      activeLayerIndex: -1,
      nextLayerId: 1,
      
      // 图层画布缓存
      layerCanvases: {}, // 存储每个图层的离屏画布
      highResLayerCanvases: {}, // 存储高分辨率图层画布
      
      // 画布尺寸
      displayCanvasSize: 1440, // 显示用画布尺寸
      highResCanvasSize: 3000, // 高分辨率尺寸（用于导出）
      
      // 预设动作
      presetActions: presetActions,
      selectedPreset: null,
      isExecutingPreset: false,
      
      // 历史状态
      canvasHistory: [],
      maxHistorySteps: 10,
      
      // 工具参数
      blurLevel: 0,
      opacity: 100,
      scale: 100,
      
      // 绘图状态
      isDrawing: false,
      lastX: 0,
      lastY: 0,
      currentTool: {
        type: 'eraser',
        size: 10,
        hardness: 50,
        active: false
      },
      
      // 滤镜状态
      activeFilters: {
        blur: 0,
        brightness: 100,
        contrast: 100,
        saturation: 100
      },
      
      // 混合模式
      blendMode: 'normal',
      
      // 图层工具选项
      showStrokeOptions: false,
      strokeOptions: {
        size: 3,
        color: '#ffffff',
        gradientIntensity: 50 // 添加渐变强度选项，默认50%
      },
      
      // 图标
      icons: {
        look: lookIcon,
        unlook: unlookIcon,
        deleate: deleateIcon
      },
      
      // 选区相关
      hasSelection: false,
      selectionData: null,
      
      // 填充选项
      showFillOptions: false,
      fillOptions: {
        color: '#ffffff'
      },
      
      // 缩放控制
      zoomLevel: 100,
      
      // 图像拖动相关
      isDragging: false,
      dragOffsetX: 0,
      dragOffsetY: 0,
      dragStartX: 0,
      dragStartY: 0,
      
      // 图层移动控制
      showLayerMoveControls: false,
      
      // 填充图层选项
      showFillLayerOptions: false,
      fillLayerOptions: {
        type: 'color',
        color: '#ffffff',
        image: null,
        previewUrl: null
      },
      
      // 放大选项
      showScaleOptions: false,
      scaleOptions: {
        scale: 100,
        maintainAspectRatio: true
      },
      
      // 添加动作管理相关的状态
      loadedActions: [],
      isPlayingAction: false,
      currentActionIndex: -1,
      showActionManager: false,
      
      // 选区调整选项
      showSelectionOptions: false,
      selectionOptions: {
        sizeAdjust: 0,
        smoothness: 1.5
      },
      originalSelectionData: null, // 保存原始选区数据
      showModal: false,
      cardKey: '',
      
      // 添加用户信息相关数据
      userInfo: null,
      loadingUserInfo: false,
      userInfoError: null,
      
      // 编辑动作相关
      showEditModal: false,
      editingAction: {
        name: '',
        required_images: 1,
        required_credits: 0,
        description: '',
        status: 1
      },
      
      // 添加用户动作相关数据
      previewModalVisible: false,
      previewImageUrl: '',
      currentPreviewAction: null,

      // loadingActions: false,
      // actionError: null,
      // loadedActions: [],
    }
  },
  computed: {
    isAdmin() {
      return this.userInfo && this.userInfo.role === '2';
    },
    isRegularUser() {
      return this.userInfo && this.userInfo.role === '1';
    }
  },
  mounted() {
    // 初始化画布
    this.initCanvas();
    
    // 检测窗口大小变化
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
    
    // 获取用户信息
    this.fetchUserInfo();

    // 获取用户信息和动作列表
  this.fetchUserInfo().then(() => {
    if (this.isRegularUser) {
      console.log('用户是普通用户，获取动作列表...');
      this.fetchActions();
    }
  });
  },
  beforeUnmount() {
    // 移除事件监听器
    window.removeEventListener('resize', this.checkScreenSize);

    
  },
  methods: {
    checkScreenSize() {
      // 如果屏幕宽度小于768px，认为是移动设备
      this.isMobileView = window.innerWidth < 768;
      
      // 在移动视图中默认隐藏侧边栏
      if (this.isMobileView) {
        this.sidebarVisible = false;
      } else {
        this.sidebarVisible = true;
      }
    },
    
    toggleSidebar() {
      this.sidebarVisible = !this.sidebarVisible;
    },
    
    initCanvas() {
      const canvas = this.$refs.canvas;
      const tempCanvas = this.$refs.tempCanvas;
      
      // 设置显示用画布大小
      canvas.width = this.displayCanvasSize;
      canvas.height = this.displayCanvasSize;
      tempCanvas.width = this.displayCanvasSize;
      tempCanvas.height = this.displayCanvasSize;
      
      this.ctx = canvas.getContext('2d');
      this.tempCtx = tempCanvas.getContext('2d');
      
      // 设置画布背景为灰色网格表示透明
      this.drawCheckerboard();
    },
    
    drawCheckerboard() {
      // 绘制棋盘格背景表示透明区域
      const canvas = this.$refs.canvas;
      const squareSize = 10;
      this.ctx.fillStyle = '#f0f0f0';
      this.ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      this.ctx.fillStyle = '#e0e0e0';
      for (let x = 0; x < canvas.width; x += squareSize) {
        for (let y = 0; y < canvas.height; y += squareSize) {
          if ((x / squareSize + y / squareSize) % 2 === 0) {
            this.ctx.fillRect(x, y, squareSize, squareSize);
          }
        }
      }
    },
    
    saveCanvasState() {
      // 保存当前状态到历史记录
      console.log('Saving canvas state...');
      
      // 创建所有图层画布的深拷贝
      const layerCanvasesState = {};
      const highResLayerCanvasesState = {};
      
      // 保存每个图层的画布状态
      for (const layerId in this.layerCanvases) {
        const originalCanvas = this.layerCanvases[layerId];
        const highResCanvas = this.highResLayerCanvases[layerId];
        
        // 创建新的画布并复制内容 - 显示画布
        if (originalCanvas) {
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = originalCanvas.width;
          tempCanvas.height = originalCanvas.height;
          
          const tempCtx = tempCanvas.getContext('2d');
          tempCtx.drawImage(originalCanvas, 0, 0);
          
          layerCanvasesState[layerId] = tempCanvas.toDataURL('image/png');
        }
        
        // 保存高分辨率画布状态 - 只存储参考，不保存完整数据避免内存占用过大
        if (highResCanvas) {
          highResLayerCanvasesState[layerId] = true;
        }
      }
      
      // 保存图层数据结构的拷贝和图层画布状态
      const state = {
        layers: JSON.parse(JSON.stringify(this.layers)),
        layerCanvases: layerCanvasesState,
        highResLayerCanvases: highResLayerCanvasesState,
        activeLayerIndex: this.activeLayerIndex
      };
      
      this.canvasHistory.push(state);
      console.log('State saved. History length:', this.canvasHistory.length);
      
      // 限制历史记录长度
      if (this.canvasHistory.length > this.maxHistorySteps) {
        this.canvasHistory.shift();
      }
    },
    
    undoLastOperation() {
      if (this.canvasHistory.length === 0) {
        console.log('No history to undo');
        return;
      }
      
      console.log('Undoing last operation...');
      
      // 从历史记录中恢复上一步
      const previousState = this.canvasHistory.pop();
      
      // 恢复图层状态
      this.layers = previousState.layers;
      this.activeLayerIndex = previousState.activeLayerIndex;
      
      console.log('Restoring layers:', this.layers.length, 'Active layer:', this.activeLayerIndex);
      
      // 恢复每个图层的画布
      this.layerCanvases = {};
      this.highResLayerCanvases = {};
      
      // 重新创建每个图层的画布
      let loadedCount = 0;
      const totalLayers = Object.keys(previousState.layerCanvases).length;
      
      for (const layerId in previousState.layerCanvases) {
        const imageData = previousState.layerCanvases[layerId];
        const img = new Image();
        
        img.onload = () => {
          // 恢复显示画布
          const canvas = document.createElement('canvas');
          canvas.width = this.displayCanvasSize;
          canvas.height = this.displayCanvasSize;
          
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          
          this.layerCanvases[layerId] = canvas;
          
          // 创建对应的高分辨率画布
          this.createHighResCanvasFromDisplay(layerId);
          
          loadedCount++;
          console.log(`Loaded layer canvas ${loadedCount}/${totalLayers}`);
          
          // 当所有图层画布都加载完成后重绘主画布
          if (loadedCount === totalLayers) {
            console.log('All layer canvases restored, redrawing...');
            this.redrawCanvas();
            
            // 更新当前图层的UI状态
            if (this.activeLayerIndex >= 0 && this.activeLayerIndex < this.layers.length) {
              const activeLayer = this.layers[this.activeLayerIndex];
              this.activeFilters = { ...activeLayer.filters };
              this.blendMode = activeLayer.blendMode;
            }
          }
        };
        
        img.src = imageData;
      }
    },
    
    createHighResCanvasFromDisplay(layerId) {
      // 从显示画布创建高分辨率画布
      const displayCanvas = this.layerCanvases[layerId];
      if (!displayCanvas) return;
      
      const highResCanvas = document.createElement('canvas');
      highResCanvas.width = this.highResCanvasSize;
      highResCanvas.height = this.highResCanvasSize;
      
      const ctx = highResCanvas.getContext('2d');
      ctx.clearRect(0, 0, highResCanvas.width, highResCanvas.height);
      
      // 将显示画布的内容缩放到高分辨率画布
      ctx.drawImage(
        displayCanvas,
        0, 0, displayCanvas.width, displayCanvas.height,
        0, 0, highResCanvas.width, highResCanvas.height
      );
      
      this.highResLayerCanvases[layerId] = highResCanvas;
    },
    
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file || !file.type.includes('image')) {
        alert('请选择有效的图片文件!');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = new Image();
        newImage.onload = () => {
          // 如果是第一张图片，初始化画布
          if (this.layers.length === 0) {
            // 保持原始图片尺寸信息
            this.imageWidth = newImage.width;
            this.imageHeight = newImage.height;
            this.imageLoaded = true;
            
            // 清空历史记录
            this.canvasHistory = [];
          }
          
          // 创建新图层
          this.addNewLayer(newImage, file.name);
        };
        newImage.src = e.target.result;
      };
      reader.readAsDataURL(file);
      
      // 清空input以便再次选择同一文件
      event.target.value = '';
    },
    
    addNewLayer(image, fileName) {
      // 创建图层缩略图
      const thumbnail = this.createThumbnail(image);
      
      // 创建新图层ID
      const layerId = this.nextLayerId++;
      
      // 为图层创建一个离屏画布
      this.createLayerCanvas(layerId, image);
      
      // 添加新图层到图层列表
      const newLayer = {
        id: layerId,
        name: this.getLayerName(fileName),
        image: image,
        visible: true,
        opacity: 100,
        blendMode: 'normal',
        filters: {
          blur: 0,
          brightness: 100,
          contrast: 100,
          saturation: 100
        },
        thumbnail: thumbnail,
        offsetX: 0,  // 初始化偏移量
        offsetY: 0
      };
      
      // 将新图层添加到数组的开头（最上层）而不是结尾
      this.layers.unshift(newLayer);
      
      // 设置当前图层为新添加的图层（现在是索引0）
      this.activeLayerIndex = 0;
      
      // 更新活动图层的滤镜和混合模式
      this.updateActiveLayerFilters();
      this.updateActiveLayerBlendMode();
      
      // 保存当前状态以支持撤销操作
      this.saveCanvasState();
      
      // 重绘画布
      this.redrawCanvas();
    },
    
    createLayerCanvas(layerId, image) {
      // 创建一个新的离屏画布来存储该图层的绘图内容 (用于显示)
      const layerCanvas = document.createElement('canvas');
      layerCanvas.width = this.displayCanvasSize;
      layerCanvas.height = this.displayCanvasSize;
      
      const ctx = layerCanvas.getContext('2d');
      
      // 绘制透明背景
      ctx.clearRect(0, 0, layerCanvas.width, layerCanvas.height);
      
      // 如果有初始图像，绘制到图层画布上
      if (image) {
        // 获取图像的原始尺寸
        const imgWidth = image.width;
        const imgHeight = image.height;
        
        // 计算居中位置，不进行缩放，保持原比例
        const centerX = (layerCanvas.width - imgWidth) / 2;
        const centerY = (layerCanvas.height - imgHeight) / 2;
        
        // 如果图像太大，超出画布，才进行适度缩放
        if (imgWidth > layerCanvas.width || imgHeight > layerCanvas.height) {
          // 计算缩放比例，使图片适应画布
          const hRatio = layerCanvas.width / imgWidth;
          const vRatio = layerCanvas.height / imgHeight;
          const ratio = Math.min(hRatio, vRatio);
          
          // 按比例缩放后的尺寸
          const scaledWidth = imgWidth * ratio;
          const scaledHeight = imgHeight * ratio;
          
          // 计算居中位置
          const scaledCenterX = (layerCanvas.width - scaledWidth) / 2;
          const scaledCenterY = (layerCanvas.height - scaledHeight) / 2;
          
          // 绘制图像到图层画布
          ctx.drawImage(
            image, 
            0, 0, imgWidth, imgHeight,
            scaledCenterX, scaledCenterY, scaledWidth, scaledHeight
          );
        } else {
          // 图像不需要缩放，直接绘制
          ctx.drawImage(image, centerX, centerY);
        }
      }
      
      // 将创建的画布存储到图层画布缓存中
      this.layerCanvases[layerId] = layerCanvas;
      
      // 同时创建高分辨率版本
      this.createHighResLayerCanvas(layerId, image);
      
      return layerCanvas;
    },
    
    createHighResLayerCanvas(layerId, image) {
      // 创建高分辨率画布
      const highResCanvas = document.createElement('canvas');
      highResCanvas.width = this.highResCanvasSize;
      highResCanvas.height = this.highResCanvasSize;
      
      const ctx = highResCanvas.getContext('2d');
      
      // 绘制透明背景
      ctx.clearRect(0, 0, highResCanvas.width, highResCanvas.height);
      
      // 如果有初始图像，绘制到高分辨率画布上
      if (image) {
        // 获取图像的原始尺寸
        const imgWidth = image.width;
        const imgHeight = image.height;
        
        // 计算在高分辨率画布上的显示比例
        const displayToHighResRatio = this.highResCanvasSize / this.displayCanvasSize;
        
        // 如果图像太大，超出原画布，使用相同的缩放策略
        if (imgWidth > this.displayCanvasSize || imgHeight > this.displayCanvasSize) {
          // 计算缩放比例，使图片适应原画布
          const hRatio = this.displayCanvasSize / imgWidth;
          const vRatio = this.displayCanvasSize / imgHeight;
          const ratio = Math.min(hRatio, vRatio);
          
          // 按比例缩放后的尺寸
          const scaledWidth = imgWidth * ratio * displayToHighResRatio;
          const scaledHeight = imgHeight * ratio * displayToHighResRatio;
          
          // 计算居中位置
          const scaledCenterX = (highResCanvas.width - scaledWidth) / 2;
          const scaledCenterY = (highResCanvas.height - scaledHeight) / 2;
          
          // 绘制图像到高分辨率画布
          ctx.drawImage(
            image, 
            0, 0, imgWidth, imgHeight,
            scaledCenterX, scaledCenterY, scaledWidth, scaledHeight
          );
        } else {
          // 图像不需要缩放，只需适应高分辨率
          const centerX = (highResCanvas.width - imgWidth * displayToHighResRatio) / 2;
          const centerY = (highResCanvas.height - imgHeight * displayToHighResRatio) / 2;
          
          ctx.drawImage(
            image, 
            0, 0, imgWidth, imgHeight,
            centerX, centerY, imgWidth * displayToHighResRatio, imgHeight * displayToHighResRatio
          );
        }
      }
      
      // 存储高分辨率画布
      this.highResLayerCanvases[layerId] = highResCanvas;
    },
    
    getActiveLayerCanvas() {
      // 获取当前活动图层的画布
      if (this.activeLayerIndex >= 0 && this.activeLayerIndex < this.layers.length) {
        const activeLayer = this.layers[this.activeLayerIndex];
        return this.layerCanvases[activeLayer.id];
      }
      return null;
    },
    
    getLayerName(fileName) {
      // 从文件名生成图层名
      if (fileName) {
        const name = fileName.split('.')[0];
        return name.length > 15 ? name.substring(0, 15) + '...' : name;
      }
      return `图层 ${this.nextLayerId}`;
    },
    
    createThumbnail(image) {
      // 创建图层缩略图
      const canvas = document.createElement('canvas');
      const size = 50; // 缩略图大小
      canvas.width = size;
      canvas.height = size;
      
      const ctx = canvas.getContext('2d');
      
      // 计算缩放比例，使图片适应缩略图
      const hRatio = size / image.width;
      const vRatio = size / image.height;
      const ratio = Math.min(hRatio, vRatio);
      
      // 计算居中位置
      const centerX = (size - image.width * ratio) / 2;
      const centerY = (size - image.height * ratio) / 2;
      
      // 绘制灰白相间的背景表示透明
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(0, 0, size, size);
      
      ctx.fillStyle = '#e0e0e0';
      const squareSize = 5;
      for (let x = 0; x < size; x += squareSize) {
        for (let y = 0; y < size; y += squareSize) {
          if ((x / squareSize + y / squareSize) % 2 === 0) {
            ctx.fillRect(x, y, squareSize, squareSize);
          }
        }
      }
      
      // 绘制缩略图
      ctx.drawImage(
        image, 
        0, 0, image.width, image.height,
        centerX, centerY, image.width * ratio, image.height * ratio
      );
      
      return canvas.toDataURL('image/png');
    },
    
    setActiveLayer(index) {
      if (index >= 0 && index < this.layers.length) {
        // 保存当前状态
        this.saveCanvasState();
        
        this.activeLayerIndex = index;
        
        // 更新UI以反映当前图层的设置
        const activeLayer = this.layers[this.activeLayerIndex];
        this.blendMode = activeLayer.blendMode;
        this.activeFilters = { ...activeLayer.filters };
        
        // 重绘画布
        this.redrawCanvas();
      }
    },
    
    toggleLayerVisibility(index) {
      if (index >= 0 && index < this.layers.length) {
        // 保存当前状态
        this.saveCanvasState();
        
        // 切换可见性
        this.layers[index].visible = !this.layers[index].visible;
        
        // 重绘画布
        this.redrawCanvas();
      }
    },
    
    updateLayerOpacity(index) {
      if (index >= 0 && index < this.layers.length) {
        // 保存当前状态
        this.saveCanvasState();
        
        // 记录动作（旧方式，向后兼容）
        this.recordAction('updateLayerOpacity', {
          layerIndex: index,
          opacity: this.layers[index].opacity
        });
        
        // 重绘画布
        this.redrawCanvas();
        
        // 捕获状态快照
        if (this.isRecording) {
          this.captureSnapshot(`更新图层 ${this.layers[index].name} 的透明度: ${this.layers[index].opacity}%`);
        }
      }
    },
    
    deleteLayer(index) {
      if (index >= 0 && index < this.layers.length) {
        // 保存当前状态
        this.saveCanvasState();
        
        // 获取要删除的图层ID
        const layerId = this.layers[index].id;
        
        // 删除图层
        this.layers.splice(index, 1);
        
        // 从缓存中删除图层画布
        delete this.layerCanvases[layerId];
        
        // 调整活动图层索引
        if (this.layers.length === 0) {
          this.activeLayerIndex = -1;
          this.imageLoaded = false;
        } else if (this.activeLayerIndex >= this.layers.length) {
          this.activeLayerIndex = this.layers.length - 1;
        }
        
        // 重绘画布
        this.redrawCanvas();
      }
    },
    
    // 图层排序方法
    moveLayerUp(index) {
      if (index <= 0 || index >= this.layers.length) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      // 记录动作（旧方式，向后兼容）
      this.recordAction('moveLayerUp', {
        layerIndex: index
      });
      
      // 交换当前图层和上一个图层
      const temp = this.layers[index];
      this.layers[index] = this.layers[index - 1];
      this.layers[index - 1] = temp;
      
      // 如果当前选中的是被移动的图层，更新选中索引
      if (this.activeLayerIndex === index) {
        this.activeLayerIndex = index - 1;
      } else if (this.activeLayerIndex === index - 1) {
        this.activeLayerIndex = index;
      }
      
      // 重绘画布
      this.redrawCanvas();
      
      // 捕获状态快照
      if (this.isRecording) {
        this.captureSnapshot(`上移图层 ${temp.name}`);
      }
    },
    
    moveLayerDown(index) {
      if (index < 0 || index >= this.layers.length - 1) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      // 记录动作（旧方式，向后兼容）
      this.recordAction('moveLayerDown', {
        layerIndex: index
      });
      
      // 交换当前图层和下一个图层
      const temp = this.layers[index];
      this.layers[index] = this.layers[index + 1];
      this.layers[index + 1] = temp;
      
      // 如果当前选中的是被移动的图层，更新选中索引
      if (this.activeLayerIndex === index) {
        this.activeLayerIndex = index + 1;
      } else if (this.activeLayerIndex === index + 1) {
        this.activeLayerIndex = index;
      }
      
      // 重绘画布
      this.redrawCanvas();
      
      // 捕获状态快照
      if (this.isRecording) {
        this.captureSnapshot(`下移图层 ${temp.name}`);
      }
    },
    
    drawImage() {
      // 重绘所有图层
      this.redrawCanvas();
    },
    
    updateBlendMode(event) {
      if (this.activeLayerIndex < 0 || !this.imageLoaded) return;
      
      try {
        // 保存当前状态
        this.saveCanvasState();
        
        // 记录动作（旧方式，向后兼容）
        this.recordAction('updateBlendMode', {
          layerIndex: this.activeLayerIndex,
          blendMode: event.target.value
        });
        
        // 更新混合模式
        this.blendMode = event.target.value;
        this.updateActiveLayerBlendMode();
        
        // 重绘画布
        this.redrawCanvas();
        
        // 捕获状态快照
        if (this.isRecording) {
          const layer = this.layers[this.activeLayerIndex];
          this.captureSnapshot(`更新图层 ${layer.name} 的混合模式: ${this.blendMode}`);
        }
      } catch (error) {
        console.error('更新混合模式时出错:', error);
      }
    },
    
    updateActiveLayerBlendMode() {
      if (this.activeLayerIndex < 0 || this.activeLayerIndex >= this.layers.length) {
        console.warn('没有选中的图层，无法应用混合模式');
        return;
      }
      
      const activeLayer = this.layers[this.activeLayerIndex];
      if (!activeLayer) {
        console.warn('选中的图层无效');
        return;
      }
      
      // 更新图层的混合模式
      activeLayer.blendMode = this.blendMode;
      
      console.log('已更新图层混合模式:', activeLayer.blendMode);
    },
    
    updateFilters() {
      if (this.activeLayerIndex < 0 || !this.imageLoaded) return;
      
      try {
        // 保存当前状态
        this.saveCanvasState();
        
        // 记录动作（旧方式，向后兼容）
        this.recordAction('updateFilters', {
          layerIndex: this.activeLayerIndex,
          filters: { ...this.activeFilters }
        });
        
        // 更新滤镜
        this.updateActiveLayerFilters();
        
        // 重绘画布
        this.redrawCanvas();
        
        // 捕获状态快照
        if (this.isRecording) {
          const layer = this.layers[this.activeLayerIndex];
          this.captureSnapshot(`更新图层 ${layer.name} 的滤镜设置`);
        }
      } catch (error) {
        console.error('更新滤镜时出错:', error);
      }
    },
    
    updateActiveLayerFilters() {
      if (this.activeLayerIndex < 0 || this.activeLayerIndex >= this.layers.length) {
        console.warn('没有选中的图层，无法应用滤镜');
        return;
      }
      
      const activeLayer = this.layers[this.activeLayerIndex];
      if (!activeLayer) {
        console.warn('选中的图层无效');
        return;
      }
      
      if (!activeLayer.filters) {
        activeLayer.filters = {
          blur: 0,
          brightness: 100,
          contrast: 100,
          saturation: 100
        };
      }
      
      // 更新图层的滤镜设置
      activeLayer.filters = { ...this.activeFilters };
      
      console.log('已更新图层滤镜:', activeLayer.filters);
    },
    
    redrawCanvas() {
      // 清除画布
      this.ctx.clearRect(0, 0, this.$refs.canvas.width, this.$refs.canvas.height);
      this.drawCheckerboard();
      
      // 如果没有图层，退出
      if (this.layers.length === 0) return;
      
      // 从下到上渲染每个图层 (索引大的图层在底部，索引小的图层在顶部)
      // 这样图层0会显示在最上方
      for (let i = this.layers.length - 1; i >= 0; i--) {
        const layer = this.layers[i];
        
        // 如果图层不可见，跳过
        if (!layer.visible) continue;
        
        // 获取图层对应的画布
        const layerCanvas = this.layerCanvases[layer.id];
        if (!layerCanvas) continue;
        
        // 设置混合模式和透明度
        this.ctx.globalCompositeOperation = layer.blendMode;
        this.ctx.globalAlpha = layer.opacity / 100;
        
        // 应用滤镜
        let filterString = '';
        if (layer.filters.blur > 0) {
          filterString += `blur(${layer.filters.blur}px) `;
        }
        if (layer.filters.brightness !== 100) {
          filterString += `brightness(${layer.filters.brightness}%) `;
        }
        if (layer.filters.contrast !== 100) {
          filterString += `contrast(${layer.filters.contrast}%) `;
        }
        if (layer.filters.saturation !== 100) {
          filterString += `saturate(${layer.filters.saturation}%) `;
        }
        
        if (filterString) {
          this.ctx.filter = filterString;
        }
        
        // 获取图层偏移量
        const offsetX = layer.offsetX || 0;
        const offsetY = layer.offsetY || 0;
        
        // 根据缩放级别计算绘制参数
        const zoomFactor = this.zoomLevel / 100;
        const displaySize = this.displayCanvasSize;
        const scaledSize = displaySize * zoomFactor;
        
        // 计算基础偏移（包括缩放和拖动）
        const baseOffsetX = (displaySize - scaledSize) / 2 + this.dragOffsetX;
        const baseOffsetY = (displaySize - scaledSize) / 2 + this.dragOffsetY;
        
        // 计算最终偏移（包括图层自身偏移）
        const finalOffsetX = baseOffsetX + (offsetX * zoomFactor);
        const finalOffsetY = baseOffsetY + (offsetY * zoomFactor);
        
        // 绘制图层画布到主画布，应用缩放、拖动偏移和图层偏移
        this.ctx.drawImage(
          layerCanvas, 
          0, 0, layerCanvas.width, layerCanvas.height,
          finalOffsetX, finalOffsetY, scaledSize, scaledSize
        );
        
        // 重置设置
        this.ctx.filter = 'none';
        this.ctx.globalCompositeOperation = 'source-over';
        this.ctx.globalAlpha = 1;
      }
    },
    
    applyCurrentFilters() {
      // 已被图层系统替代
      this.updateActiveLayerFilters();
      this.redrawCanvas();
    },
    
    resetEditState() {
      // 重置当前图层的参数
      if (this.activeLayerIndex >= 0) {
        const activeLayer = this.layers[this.activeLayerIndex];
        activeLayer.filters = {
          blur: 0,
          brightness: 100,
          contrast: 100,
          saturation: 100
        };
        activeLayer.blendMode = 'normal';
        
        // 同步UI
        this.activeFilters = { ...activeLayer.filters };
        this.blendMode = activeLayer.blendMode;
      } else {
        // 重置通用参数
        this.activeFilters = {
          blur: 0,
          brightness: 100,
          contrast: 100,
          saturation: 100
        };
        this.blendMode = 'normal';
      }
    },
    
    resetCanvas() {
      if (this.layers.length === 0) return;
      
      console.log('Resetting canvas...');
      
      // 保存当前状态
      this.saveCanvasState();
      
      try {
        // 重置所有图层
        for (let i = 0; i < this.layers.length; i++) {
          const layer = this.layers[i];
          
          // 重置图层参数
          layer.filters = {
            blur: 0,
            brightness: 100,
            contrast: 100,
            saturation: 100
          };
          layer.blendMode = 'normal';
          layer.opacity = 100;
          
          // 重新创建图层画布，使用原始图像
          if (layer.image) {
            // 完全重新创建图层画布
            const layerCanvas = document.createElement('canvas');
            layerCanvas.width = this.$refs.canvas.width;
            layerCanvas.height = this.$refs.canvas.height;
            
            const ctx = layerCanvas.getContext('2d');
            
            // 清除画布
            ctx.clearRect(0, 0, layerCanvas.width, layerCanvas.height);
            
            // 获取图像的原始尺寸
            const imgWidth = layer.image.width;
            const imgHeight = layer.image.height;
            
            // 计算居中位置，不进行缩放，保持原比例
            const centerX = (layerCanvas.width - imgWidth) / 2;
            const centerY = (layerCanvas.height - imgHeight) / 2;
            
            // 如果图像太大，超出画布，才进行适度缩放
            if (imgWidth > layerCanvas.width || imgHeight > layerCanvas.height) {
              // 计算缩放比例，使图片适应画布
              const hRatio = layerCanvas.width / imgWidth;
              const vRatio = layerCanvas.height / imgHeight;
              const ratio = Math.min(hRatio, vRatio);
              
              // 按比例缩放后的尺寸
              const scaledWidth = imgWidth * ratio;
              const scaledHeight = imgHeight * ratio;
              
              // 计算居中位置
              const scaledCenterX = (layerCanvas.width - scaledWidth) / 2;
              const scaledCenterY = (layerCanvas.height - scaledHeight) / 2;
              
              // 绘制原始图像到图层画布
              ctx.drawImage(
                layer.image, 
                0, 0, imgWidth, imgHeight,
                scaledCenterX, scaledCenterY, scaledWidth, scaledHeight
              );
            } else {
              // 图像不需要缩放，直接绘制
              ctx.drawImage(layer.image, centerX, centerY);
            }
            
            // 替换原有的图层画布
            this.layerCanvases[layer.id] = layerCanvas;
            
            // 更新图层缩略图
            this.updateLayerThumbnail(i);
          }
        }
        
        // 重置UI参数
        if (this.activeLayerIndex >= 0) {
          this.activeFilters = {
            blur: 0,
            brightness: 100,
            contrast: 100,
            saturation: 100
          };
          this.blendMode = 'normal';
        }
        
        // 重绘画布
        this.redrawCanvas();
        
        console.log('Canvas reset completed');
      } catch (e) {
        console.error('Error during canvas reset:', e);
      }
    },
    
    downloadImage() {
      if (!this.imageLoaded) return;

      try {
        // 提供多种下载选项
        const options = ['当前画布尺寸', '原始图像尺寸', '自定义尺寸'];
        let choice = -1;
        
        do {
          const input = prompt(
            `请选择导出尺寸选项 (输入数字):\n` +
            `1. 当前画布尺寸 (${this.displayCanvasSize}×${this.displayCanvasSize}像素)\n` +
            `2. 原始图像尺寸 (${this.imageWidth}×${this.imageHeight}像素)\n` + 
            `3. 自定义尺寸`,
            '1'
          );
          
          // 用户取消
          if (input === null) return;
          
          choice = parseInt(input);
        } while (isNaN(choice) || choice < 1 || choice > options.length);
        
        // 确定导出尺寸
        let exportWidth, exportHeight;
        
        if (choice === 1) {
          // 当前画布尺寸
          exportWidth = this.displayCanvasSize;
          exportHeight = this.displayCanvasSize;
        } else if (choice === 2) {
          // 原始图像尺寸
          exportWidth = this.imageWidth;
          exportHeight = this.imageHeight;
        } else {
          // 自定义尺寸
          const size = prompt('请输入导出尺寸 (例如: 1024×768):', `${this.imageWidth}×${this.imageHeight}`);
          if (!size) return; // 用户取消
          
          const dimensions = size.split(/[x×]/);
          if (dimensions.length !== 2) {
            alert('格式无效，请使用 宽度×高度 格式');
            return;
          }
          
          exportWidth = parseInt(dimensions[0]);
          exportHeight = parseInt(dimensions[1]);
          
          if (isNaN(exportWidth) || isNaN(exportHeight) || exportWidth <= 0 || exportHeight <= 0) {
            alert('请输入有效的尺寸');
            return;
          }
        }
        
        // 创建导出画布
        const exportCanvas = document.createElement('canvas');
        exportCanvas.width = exportWidth;
        exportCanvas.height = exportHeight;
        const exportCtx = exportCanvas.getContext('2d');
        
        // 优化导出画质
        exportCtx.imageSmoothingEnabled = true;
        exportCtx.imageSmoothingQuality = 'high';
        
        // 确定是否应用棋盘格透明背景
        const useTransparentBg = confirm('是否保留透明背景？点击"确定"保留透明背景，点击"取消"使用白色背景');
        
        // 设置背景
        if (!useTransparentBg) {
          // 使用白色背景
          exportCtx.fillStyle = '#ffffff';
          exportCtx.fillRect(0, 0, exportWidth, exportHeight);
        } else {
          // 绘制透明背景的棋盘格
          this.drawCheckerboardOnCanvas(exportCanvas);
        }
        
        // 计算缩放和居中参数
        const aspectRatio = this.imageWidth / this.imageHeight;
        const canvasAspectRatio = exportWidth / exportHeight;
        
        let drawWidth, drawHeight, offsetX, offsetY;
        
        if (aspectRatio > canvasAspectRatio) {
          // 图像比导出画布更宽，以宽度为准
          drawWidth = exportWidth;
          drawHeight = exportWidth / aspectRatio;
          offsetX = 0;
          offsetY = (exportHeight - drawHeight) / 2;
        } else {
          // 图像比导出画布更高，以高度为准
          drawHeight = exportHeight;
          drawWidth = exportHeight * aspectRatio;
          offsetX = (exportWidth - drawWidth) / 2;
          offsetY = 0;
        }
        
        // 创建临时画布用于合成最终效果
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = this.displayCanvasSize;
        tempCanvas.height = this.displayCanvasSize;
        const tempCtx = tempCanvas.getContext('2d');
        
        // 从下到上渲染每个图层
        for (let i = this.layers.length - 1; i >= 0; i--) {
          const layer = this.layers[i];
          
          // 如果图层不可见，跳过
          if (!layer.visible) continue;
          
          // 获取图层对应的画布
          const layerCanvas = this.layerCanvases[layer.id];
          if (!layerCanvas) continue;
          
          // 设置混合模式和透明度
          tempCtx.globalCompositeOperation = layer.blendMode;
          tempCtx.globalAlpha = layer.opacity / 100;
          
          // 应用滤镜
          let filterString = '';
          if (layer.filters.blur > 0) {
            filterString += `blur(${layer.filters.blur}px) `;
          }
          if (layer.filters.brightness !== 100) {
            filterString += `brightness(${layer.filters.brightness}%) `;
          }
          if (layer.filters.contrast !== 100) {
            filterString += `contrast(${layer.filters.contrast}%) `;
          }
          if (layer.filters.saturation !== 100) {
            filterString += `saturate(${layer.filters.saturation}%) `;
          }
          
          if (filterString) {
            tempCtx.filter = filterString;
          }
          
          // 获取图层偏移量
          const layerOffsetX = layer.offsetX || 0;
          const layerOffsetY = layer.offsetY || 0;
          
          // 绘制图层到临时画布
          tempCtx.drawImage(
            layerCanvas,
            layerOffsetX, layerOffsetY
          );
          
          // 重置设置
          tempCtx.filter = 'none';
          tempCtx.globalCompositeOperation = 'source-over';
          tempCtx.globalAlpha = 1;
        }
        
        // 将临时画布的内容缩放绘制到导出画布
        exportCtx.drawImage(
          tempCanvas,
          0, 0, tempCanvas.width, tempCanvas.height,
          offsetX, offsetY, drawWidth, drawHeight
        );
        
        // 选择导出质量和格式
        const formatOptions = ['PNG (无损)', 'JPEG (较小)'];
        let formatChoice = -1;
        
        do {
          const input = prompt(
            `请选择导出格式 (输入数字):\n` +
            `1. PNG (无损，支持透明)\n` +
            `2. JPEG (文件较小，不支持透明)`,
            '1'
          );
          
          // 用户取消
          if (input === null) return;
          
          formatChoice = parseInt(input);
        } while (isNaN(formatChoice) || formatChoice < 1 || formatChoice > formatOptions.length);
        
        let mimeType, quality, extension;
        
        if (formatChoice === 1) {
          // PNG
          mimeType = 'image/png';
          quality = 1.0;
          extension = 'png';
        } else {
          // JPEG
          mimeType = 'image/jpeg';
          // 提示用户选择JPEG质量
          const qualityStr = prompt('请输入JPEG质量 (1-100):', '90');
          if (!qualityStr) return; // 用户取消
          
          const qualityNum = parseInt(qualityStr);
          if (isNaN(qualityNum) || qualityNum < 1 || qualityNum > 100) {
            alert('请输入1-100之间的数字');
            return;
          }
          
          quality = qualityNum / 100;
          extension = 'jpg';
        }
        
        // 转换为Blob对象
        exportCanvas.toBlob((blob) => {
          if (!blob) {
            alert('导出图像失败，请尝试不同的尺寸或格式');
            return;
          }
          
          // 使用URL.createObjectURL创建临时URL
          const url = URL.createObjectURL(blob);
          
          // 创建文件名
          const dimensions = `${exportWidth}x${exportHeight}`;
          const fileName = `image_${dimensions}.${extension}`;
          
          // 创建下载链接
          const link = document.createElement('a');
          link.download = fileName;
          link.href = url;
          
          // 触发下载
          document.body.appendChild(link);
          link.click();
          
          // 清理DOM和释放URL
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(url), 100);
          
        }, mimeType, quality);
        
      } catch (error) {
        console.error('下载图像时出错:', error);
        alert('下载图像失败: ' + error.message);
      }
    },
    
    drawCheckerboardOnCanvas(canvas) {
      // 在指定画布上绘制棋盘格背景
      const ctx = canvas.getContext('2d');
      const squareSize = canvas.width / 30; // 根据画布大小调整格子大小
      
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.fillStyle = '#e0e0e0';
      for (let x = 0; x < canvas.width; x += squareSize) {
        for (let y = 0; y < canvas.height; y += squareSize) {
          if ((x / squareSize + y / squareSize) % 2 === 0) {
            ctx.fillRect(x, y, squareSize, squareSize);
          }
        }
      }
    },
    
    // 鼠标绘图功能
    startDraw(e) {
      // 如果当前是拖动模式，交给startDrag处理
      if (this.zoomLevel > 100 && !this.currentTool.active) {
        this.startDrag(e);
        return;
      }
      
      if (!this.imageLoaded || this.activeLayerIndex < 0 || !this.currentTool.active) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      this.isDrawing = true;
      
      const rect = this.$refs.canvas.getBoundingClientRect();
      
      // 计算准确的鼠标坐标，考虑设备像素比、画布缩放和用户设置的缩放
      const scaleX = this.$refs.canvas.width / rect.width;
      const scaleY = this.$refs.canvas.height / rect.height;
      const zoomFactor = this.zoomLevel / 100;
      
      // 调整坐标以适应缩放和拖动
      const canvasCenterX = rect.width / 2;
      const canvasCenterY = rect.height / 2;
      const relativeX = (e.clientX - rect.left - canvasCenterX) / zoomFactor + canvasCenterX - this.dragOffsetX / zoomFactor;
      const relativeY = (e.clientY - rect.top - canvasCenterY) / zoomFactor + canvasCenterY - this.dragOffsetY / zoomFactor;
      
      this.lastX = relativeX * scaleX;
      this.lastY = relativeY * scaleY;
    },
    
    drawing(e) {
      // 如果当前是拖动模式，交给doDrag处理
      if (this.isDragging) {
        this.doDrag(e);
        return;
      }
      
      if (!this.isDrawing) return;
      
      const rect = this.$refs.canvas.getBoundingClientRect();
      
      // 计算准确的鼠标坐标，考虑设备像素比、画布缩放和用户设置的缩放
      const scaleX = this.$refs.canvas.width / rect.width;
      const scaleY = this.$refs.canvas.height / rect.height;
      const zoomFactor = this.zoomLevel / 100;
      
      // 调整坐标以适应缩放和拖动
      const canvasCenterX = rect.width / 2;
      const canvasCenterY = rect.height / 2;
      const relativeX = (e.clientX - rect.left - canvasCenterX) / zoomFactor + canvasCenterX - this.dragOffsetX / zoomFactor;
      const relativeY = (e.clientY - rect.top - canvasCenterY) / zoomFactor + canvasCenterY - this.dragOffsetY / zoomFactor;
      
      const x = relativeX * scaleX;
      const y = relativeY * scaleY;
      
      this.performDrawing(x, y);
    },
    
    performDrawing(x, y) {
      // 获取当前活动图层的画布
      const layerCanvas = this.getActiveLayerCanvas();
      if (!layerCanvas) return;
      
      const layerCtx = layerCanvas.getContext('2d');
      
      // 根据当前工具执行不同操作
      if (this.currentTool.type === 'eraser') {
        layerCtx.globalCompositeOperation = 'destination-out';
        // 根据硬度值设置不同的透明度
        layerCtx.globalAlpha = this.currentTool.hardness / 100;
      } else {
        layerCtx.globalCompositeOperation = 'source-over';
        layerCtx.globalAlpha = 1;
      }
      
      layerCtx.beginPath();
      layerCtx.lineWidth = this.currentTool.size;
      layerCtx.lineCap = 'round';
      layerCtx.lineJoin = 'round';
      
      if (this.currentTool.type === 'brush') {
        layerCtx.strokeStyle = '#000000';
      }
      
      layerCtx.moveTo(this.lastX, this.lastY);
      layerCtx.lineTo(x, y);
      layerCtx.stroke();
      
      // 恢复默认透明度
      layerCtx.globalAlpha = 1;
      
      this.lastX = x;
      this.lastY = y;
      
      // 同步绘制到高分辨率画布
      this.syncDrawToHighRes();
      
      // 更新当前图层的缩略图
      if (this.activeLayerIndex >= 0) {
        this.updateLayerThumbnail(this.activeLayerIndex);
      }
      
      // 重绘主画布以显示更改
      this.redrawCanvas();
    },
    
    syncDrawToHighRes() {
      // 将显示画布的内容同步到高分辨率画布
      if (this.activeLayerIndex >= 0 && this.activeLayerIndex < this.layers.length) {
        const layer = this.layers[this.activeLayerIndex];
        const displayCanvas = this.layerCanvases[layer.id];
        const highResCanvas = this.highResLayerCanvases[layer.id];
        
        if (!displayCanvas || !highResCanvas) return;
        
        // 创建新的高分辨率画布进行合成
        this.createHighResCanvasFromDisplay(layer.id);
      }
    },
    
    updateLayerThumbnail(index) {
      // 更新图层缩略图
      if (index >= 0 && index < this.layers.length) {
        const layer = this.layers[index];
        const layerCanvas = this.layerCanvases[layer.id];
        
        if (layerCanvas) {
          // 创建缩略图
          const thumbCanvas = document.createElement('canvas');
          const size = 50;
          thumbCanvas.width = size;
          thumbCanvas.height = size;
          
          const ctx = thumbCanvas.getContext('2d');
          // 绘制灰白相间的背景表示透明
          ctx.fillStyle = '#f0f0f0';
          ctx.fillRect(0, 0, size, size);
          
          ctx.fillStyle = '#e0e0e0';
          const squareSize = 5;
          for (let x = 0; x < size; x += squareSize) {
            for (let y = 0; y < size; y += squareSize) {
              if ((x / squareSize + y / squareSize) % 2 === 0) {
                ctx.fillRect(x, y, squareSize, squareSize);
              }
            }
          }
          
          // 绘制图层内容
          ctx.drawImage(layerCanvas, 0, 0, layerCanvas.width, layerCanvas.height, 0, 0, size, size);
          
          // 更新缩略图
          layer.thumbnail = thumbCanvas.toDataURL('image/png');
        }
      }
    },
    
    // eslint-disable-next-line no-unused-vars
    endDraw(e) {
      // 结束绘图或拖动
      this.isDrawing = false;
      this.isDragging = false;
      
      // 在绘图结束时更新当前图层的缩略图
      if (this.activeLayerIndex >= 0) {
        this.updateLayerThumbnail(this.activeLayerIndex);
      }
    },
    
    selectTool(toolType) {
      this.currentTool.type = toolType;
      this.currentTool.active = true;
    },
    
    toggleEraser() {
      // 如果橡皮擦当前未激活，则激活它
      // 如果已经激活，则取消激活
      this.currentTool.active = !this.currentTool.active;
      this.currentTool.type = 'eraser';
    },
    
    updateToolSize() {
      // 工具大小变化时不需要保存状态
    },
    
    updateToolHardness() {
      // 硬度变化时不需要保存状态
    },
    
    executePreset() {
      console.log('执行预设，当前选择:', this.selectedPreset, typeof this.selectedPreset);
      
      // 检查是否选择了预设动作
      if (!this.selectedPreset) {
        alert('请选择预设动作');
        return;
      }
      
      // 获取选中的预设对象
      const selectedPreset = this.presetActions.find(p => p.id === this.selectedPreset);
      console.log('获取到的预设对象:', selectedPreset);
      
      if (!selectedPreset) {
        alert('无效的预设动作');
        return;
      }
      
      // 检查图层数量是否满足要求
      if (this.layers.length < selectedPreset.requiredLayers) {
        alert(`此预设动作需要至少${selectedPreset.requiredLayers}张图片，请先上传图片`);
        return;
      }
      
      // 执行预设动作
      this.isExecutingPreset = true;
      this.saveCanvasState(); // 保存当前状态以便撤销
      
      try {
        // 创建上下文对象，包含预设动作需要的方法和数据
        const context = {
          layers: this.layers,
          layerCanvases: this.layerCanvases,
          activeLayerIndex: this.activeLayerIndex,
          updateLayerThumbnail: this.updateLayerThumbnail,
          redrawCanvas: this.redrawCanvas
        };
        
        // 使用导入的executePreset函数
        const result = executePreset(this.selectedPreset, context);
        
        if (!result) {
          console.error('预设动作执行失败');
          alert('预设动作执行失败，请查看控制台获取详细信息');
        }
      } catch (error) {
        console.error('执行预设动作时出错:', error);
        alert(`执行预设动作出错: ${error.message}`);
      } finally {
        this.isExecutingPreset = false;
      }
    },
    
    // 执行选定预设动作 (新方法)
    executeSelectedPreset() {
      if (!this.selectedPreset || this.isExecutingPreset) return;
      
      this.isExecutingPreset = true;
      
      // 保存当前状态
      this.saveCanvasState();
      
      try {
        // 执行预设动作
        executePreset(this, this.selectedPreset);
      } catch (error) {
        console.error('执行预设动作时发生错误:', error);
        alert('执行预设动作失败: ' + error.message);
      } finally {
        this.isExecutingPreset = false;
      }
    },
    
    // 缩放相关方法
    applyZoom() {
      // 应用缩放到画布
      // 重置拖动偏移，以避免放大/缩小时图像位置异常
      if (this.zoomLevel <= 100) {
        this.dragOffsetX = 0;
        this.dragOffsetY = 0;
      }
      this.redrawCanvas();
    },
    
    zoomIn() {
      // 放大画布
      this.zoomLevel = Math.min(200, this.zoomLevel + 10);
      this.applyZoom();
    },
    
    zoomOut() {
      // 缩小画布
      this.zoomLevel = Math.max(10, this.zoomLevel - 10);
      this.applyZoom();
    },
    
    resetZoom() {
      // 重置缩放级别
      this.zoomLevel = 100;
      this.dragOffsetX = 0;
      this.dragOffsetY = 0;
      this.applyZoom();
    },
    
    // 图层工具方法
    invertLayer() {
      if (this.activeLayerIndex < 0) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      // 执行操作
      const layer = this.layers[this.activeLayerIndex];
      const layerCanvas = this.layerCanvases[layer.id];
      
      if (!layerCanvas) return;
      
      const ctx = layerCanvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, layerCanvas.width, layerCanvas.height);
      const data = imageData.data;
      
      // 反转颜色（跳过透明像素）
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] > 0) { // 只处理不透明像素
          data[i] = 255 - data[i];       // R
          data[i + 1] = 255 - data[i + 1]; // G
          data[i + 2] = 255 - data[i + 2]; // B
        }
      }
      
      // 将处理后的数据放回画布
      ctx.putImageData(imageData, 0, 0);
      
      // 更新高分辨率画布
      this.createHighResCanvasFromDisplay(layer.id);
      
      // 更新缩略图
      this.updateLayerThumbnail(this.activeLayerIndex);
      
      // 重绘主画布
      this.redrawCanvas();
      
      // 记录操作
      if (this.isRecording) {
        this.captureSnapshot(`反相图层 ${layer.name}`);
      }
      
      // 旧方式记录（向后兼容）
      this.recordAction('invertLayer', {
        layerIndex: this.activeLayerIndex
      });
    },
    
    openStrokeOptions() {
      if (this.activeLayerIndex < 0) return;
      this.showStrokeOptions = true;
    },
    
    applyStroke() {
      if (this.activeLayerIndex < 0) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      // 获取当前图层的画布
      const layer = this.layers[this.activeLayerIndex];
      const layerCanvas = this.layerCanvases[layer.id];
      
      if (!layerCanvas) return;
      
      const ctx = layerCanvas.getContext('2d');
      const strokeSize = parseInt(this.strokeOptions.size);
      const gradientIntensity = this.strokeOptions.gradientIntensity / 100; // 转换为0-1范围
      
      // 创建临时画布
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = layerCanvas.width;
      tempCanvas.height = layerCanvas.height;
      const tempCtx = tempCanvas.getContext('2d');
      
      // 复制原始内容到临时画布
      tempCtx.drawImage(layerCanvas, 0, 0);
      
      // 创建描边画布
      const strokeCanvas = document.createElement('canvas');
      strokeCanvas.width = layerCanvas.width;
      strokeCanvas.height = layerCanvas.height;
      const strokeCtx = strokeCanvas.getContext('2d');
      
      // 获取原始图像数据
      const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      const data = imageData.data;
      
      // 创建一个新的ImageData来存储描边
      const strokeData = new ImageData(tempCanvas.width, tempCanvas.height);
      const strokePixels = strokeData.data;
      
      // 计算描边范围
      const range = strokeSize * 2;
      
      // 对每个像素进行处理
      for (let y = 0; y < tempCanvas.height; y++) {
        for (let x = 0; x < tempCanvas.width; x++) {
          const idx = (y * tempCanvas.width + x) * 4;
          
          // 如果当前像素是非透明的
          if (data[idx + 3] > 20) {
            // 在描边范围内检查周围像素
            for (let dy = -range; dy <= range; dy++) {
              for (let dx = -range; dx <= range; dx++) {
                // 计算目标像素位置
                const targetX = x + dx;
                const targetY = y + dy;
                
                // 检查是否在画布范围内
                if (targetX >= 0 && targetX < tempCanvas.width &&
                    targetY >= 0 && targetY < tempCanvas.height) {
                  
                  // 计算到当前像素的距离
                  const distance = Math.sqrt(dx * dx + dy * dy);
                  
                  // 如果在描边范围内
                  if (distance <= range) {
                    const targetIdx = (targetY * tempCanvas.width + targetX) * 4;
                    
                    // 计算描边强度（基于距离的渐变）
                    let intensity;
                    if (gradientIntensity === 0) {
                      // 如果渐变强度为0，则使用硬边缘
                      intensity = distance <= range * 0.8 ? 1 : 0;
                    } else {
                      // 否则使用渐变效果，渐变强度影响渐变的陡峭程度
                      intensity = Math.pow(1 - (distance / range), 1 / gradientIntensity);
                    }
                    
                    // 设置描边像素
                    strokePixels[targetIdx] = 255;     // R
                    strokePixels[targetIdx + 1] = 255; // G
                    strokePixels[targetIdx + 2] = 255; // B
                    strokePixels[targetIdx + 3] = Math.max(strokePixels[targetIdx + 3], intensity * 255);
                  }
                }
              }
            }
          }
        }
      }
      
      // 将描边数据绘制到描边画布
      strokeCtx.putImageData(strokeData, 0, 0);
      
      // 清除原画布
      ctx.clearRect(0, 0, layerCanvas.width, layerCanvas.height);
      
      // 先绘制描边
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = this.strokeOptions.color;
      ctx.globalAlpha = 1;
      
      // 使用描边颜色
      strokeCtx.globalCompositeOperation = 'source-in';
      strokeCtx.fillStyle = this.strokeOptions.color;
      strokeCtx.fillRect(0, 0, strokeCanvas.width, strokeCanvas.height);
      
      // 绘制描边
      ctx.drawImage(strokeCanvas, 0, 0);
      
      // 绘制原始内容
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(tempCanvas, 0, 0);
      
      // 同步到高分辨率画布
      this.createHighResCanvasFromDisplay(layer.id);
      
      // 更新缩略图
      this.updateLayerThumbnail(this.activeLayerIndex);
      
      // 重绘主画布
      this.redrawCanvas();
      
      // 关闭描边选项面板
      this.showStrokeOptions = false;
    },
    
    // 复制图层方法
    duplicateLayer(index) {
      if (index < 0 || index >= this.layers.length) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      const sourceLayer = this.layers[index];
      const sourceCanvas = this.layerCanvases[sourceLayer.id];
      
      if (!sourceCanvas) return;
      
      // 创建新的图层ID
      const newLayerId = this.nextLayerId++;
      
      // 复制画布内容
      const newCanvas = document.createElement('canvas');
      newCanvas.width = sourceCanvas.width;
      newCanvas.height = sourceCanvas.height;
      const ctx = newCanvas.getContext('2d');
      ctx.drawImage(sourceCanvas, 0, 0);
      
      // 存储新画布
      this.layerCanvases[newLayerId] = newCanvas;
      
      // 创建高分辨率版本
      this.createHighResCanvasFromDisplay(newLayerId);
      
      // 创建新图层数据
      const newLayer = {
        id: newLayerId,
        name: `${sourceLayer.name} 副本`,
        image: sourceLayer.image, // 引用相同的原始图像
        visible: true,
        opacity: sourceLayer.opacity,
        blendMode: sourceLayer.blendMode,
        filters: { ...sourceLayer.filters }, // 深拷贝滤镜设置
        thumbnail: sourceLayer.thumbnail, // 暂时使用原图层的缩略图
        offsetX: sourceLayer.offsetX || 0, // 复制偏移量
        offsetY: sourceLayer.offsetY || 0
      };
      
      // 在源图层之上插入新图层
      this.layers.splice(index, 0, newLayer);
      
      // 更新缩略图
      this.updateLayerThumbnail(index);
      
      // 选中新复制的图层
      this.activeLayerIndex = index;
      
      // 重绘画布
      this.redrawCanvas();
      
      // 记录操作（旧式兼容）
      this.recordAction('duplicateLayer', {
        layerIndex: index,
        newLayerId: newLayerId
      });
      
      // 捕获状态快照
      if (this.isRecording) {
        this.captureSnapshot(`复制图层: ${sourceLayer.name}`);
      }
    },
    
    // 图层重命名方法
    renameLayer() {
      if (this.activeLayerIndex < 0 || this.activeLayerIndex >= this.layers.length) return;
      
      const layer = this.layers[this.activeLayerIndex];
      const newName = prompt('请输入新的图层名称:', layer.name);
      
      // 用户取消或输入为空，不修改
      if (newName === null || newName.trim() === '') return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      // 记录旧名称用于状态快照
      const oldName = layer.name;
      
      // 更新图层名称
      layer.name = newName.trim();
      
      // 记录操作（旧式兼容）
      this.recordAction('renameLayer', {
        layerIndex: this.activeLayerIndex,
        oldName: oldName,
        newName: layer.name
      });
      
      // 捕获状态快照
      if (this.isRecording) {
        this.captureSnapshot(`重命名图层: ${oldName} → ${layer.name}`);
      }
    },
    
    // 向下合并图层
    mergeDown() {
      // 确保当前图层不是最底层
      if (this.activeLayerIndex >= this.layers.length - 1 || this.activeLayerIndex < 0) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      const activeLayer = this.layers[this.activeLayerIndex]; // 上层（当前选中图层）
      const lowerLayer = this.layers[this.activeLayerIndex + 1]; // 下层
      
      // 确保下层图层可见
      if (!lowerLayer.visible) {
        alert('无法合并到不可见图层');
        return;
      }
      
      // 记录合并前的图层状态
      const mergeInfo = {
        upperLayerId: activeLayer.id,
        lowerLayerId: lowerLayer.id,
        upperLayerState: JSON.stringify(activeLayer),
        lowerLayerState: JSON.stringify(lowerLayer)
      };
      
      // 获取两个图层的画布
      const activeCanvas = this.layerCanvases[activeLayer.id];
      const lowerCanvas = this.layerCanvases[lowerLayer.id];
      
      if (!activeCanvas || !lowerCanvas) return;
      
      // 获取图层的偏移值
      const activeOffsetX = activeLayer.offsetX || 0;
      const activeOffsetY = activeLayer.offsetY || 0;
      const lowerOffsetX = lowerLayer.offsetX || 0;
      const lowerOffsetY = lowerLayer.offsetY || 0;
      
      // 计算合并后图层需要覆盖的区域
      const minX = Math.min(activeOffsetX, lowerOffsetX);
      const minY = Math.min(activeOffsetY, lowerOffsetY);
      const maxX = Math.max(activeOffsetX + activeCanvas.width, lowerOffsetX + lowerCanvas.width);
      const maxY = Math.max(activeOffsetY + activeCanvas.height, lowerOffsetY + lowerCanvas.height);
      
      // 创建临时画布合并图层，尺寸能够容纳所有内容
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = maxX - minX;
      tempCanvas.height = maxY - minY;
      const tempCtx = tempCanvas.getContext('2d');
      
      // 首先绘制下层图层，考虑相对偏移量
      tempCtx.globalAlpha = lowerLayer.opacity / 100;
      tempCtx.globalCompositeOperation = 'source-over';
      tempCtx.drawImage(lowerCanvas, lowerOffsetX - minX, lowerOffsetY - minY);
      
      // 然后应用上层图层（带混合模式），考虑相对偏移量
      tempCtx.globalAlpha = activeLayer.opacity / 100;
      tempCtx.globalCompositeOperation = activeLayer.blendMode;
      tempCtx.drawImage(activeCanvas, activeOffsetX - minX, activeOffsetY - minY);
      
      // 重置画布属性
      tempCtx.globalAlpha = 1;
      tempCtx.globalCompositeOperation = 'source-over';
      
      // 创建新的画布用于存储合并结果，大小与原下层图层相同
      const newCanvas = document.createElement('canvas');
      newCanvas.width = tempCanvas.width;
      newCanvas.height = tempCanvas.height;
      const newCtx = newCanvas.getContext('2d');
      
      // 将临时画布的内容复制到新画布
      newCtx.drawImage(tempCanvas, 0, 0);
      
      // 更新下层图层的画布
      this.layerCanvases[lowerLayer.id] = newCanvas;
      
      // 更新下层图层的名称和偏移量
      lowerLayer.name = `${activeLayer.name} + ${lowerLayer.name}`;
      lowerLayer.offsetX = minX;
      lowerLayer.offsetY = minY;
      
      // 更新下层图层的缩略图
      this.updateLayerThumbnail(this.activeLayerIndex + 1);
      
      // 更新高分辨率画布
      this.createHighResCanvasFromDisplay(lowerLayer.id);
      
      // 记录动作（旧方式，向后兼容）
      this.recordAction('mergeDown', {
        upperLayerIndex: this.activeLayerIndex,
        lowerLayerIndex: this.activeLayerIndex + 1,
        mergeInfo: mergeInfo
      });
      
      // 移除当前图层
      this.layers.splice(this.activeLayerIndex, 1);
      delete this.layerCanvases[activeLayer.id];
      delete this.highResLayerCanvases[activeLayer.id];
      
      // 更新活动图层索引
      this.activeLayerIndex = Math.min(this.activeLayerIndex, this.layers.length - 1);
      
      // 重绘画布
      this.redrawCanvas();
      
      // 捕获状态快照
      if (this.isRecording) {
        this.captureSnapshot(`合并图层: ${activeLayer.name} → ${lowerLayer.name}`);
      }
    },
    
    // 显示合并可见图层选项
    showMergeOptions() {
      if (this.layers.length < 2) return;
      
      if (confirm('是否合并所有可见图层？此操作无法撤销。')) {
        this.mergeVisibleLayers();
      }
    },
    
    // 合并所有可见图层
    mergeVisibleLayers() {
      // 保存当前状态
      this.saveCanvasState();
      
      // 获取所有可见图层
      const visibleLayers = this.layers.filter(layer => layer.visible);
      if (visibleLayers.length === 0) return;
      
      // 计算所有可见图层覆盖的区域
      let minX = Number.MAX_SAFE_INTEGER;
      let minY = Number.MAX_SAFE_INTEGER;
      let maxX = Number.MIN_SAFE_INTEGER;
      let maxY = Number.MIN_SAFE_INTEGER;
      
      for (const layer of visibleLayers) {
        const layerCanvas = this.layerCanvases[layer.id];
        if (!layerCanvas) continue;
        
        const offsetX = layer.offsetX || 0;
        const offsetY = layer.offsetY || 0;
        
        minX = Math.min(minX, offsetX);
        minY = Math.min(minY, offsetY);
        maxX = Math.max(maxX, offsetX + layerCanvas.width);
        maxY = Math.max(maxY, offsetY + layerCanvas.height);
      }
      
      // 如果没有找到有效的区域，直接返回
      if (minX === Number.MAX_SAFE_INTEGER || maxX === Number.MIN_SAFE_INTEGER) return;
      
      // 创建一个新的画布用于合并结果
      const mergedCanvas = document.createElement('canvas');
      mergedCanvas.width = maxX - minX;
      mergedCanvas.height = maxY - minY;
      const mergedCtx = mergedCanvas.getContext('2d');
      
      // 从下到上绘制每个可见图层
      for (let i = visibleLayers.length - 1; i >= 0; i--) {
        const layer = visibleLayers[i];
        const layerCanvas = this.layerCanvases[layer.id];
        if (!layerCanvas) continue;
        
        // 获取图层偏移
        const offsetX = layer.offsetX || 0;
        const offsetY = layer.offsetY || 0;
        
        // 应用图层的混合模式和不透明度
        mergedCtx.globalAlpha = layer.opacity / 100;
        mergedCtx.globalCompositeOperation = layer.blendMode;
        
        // 绘制图层，考虑相对偏移
        mergedCtx.drawImage(layerCanvas, offsetX - minX, offsetY - minY);
      }
      
      // 重置画布属性
      mergedCtx.globalAlpha = 1;
      mergedCtx.globalCompositeOperation = 'source-over';
      
      // 创建新的图层ID
      const newLayerId = this.nextLayerId++;
      
      // 存储合并后的画布
      this.layerCanvases[newLayerId] = mergedCanvas;
      
      // 创建对应的高分辨率画布
      this.createHighResCanvasFromDisplay(newLayerId);
      
      // 创建缩略图
      const thumbCanvas = document.createElement('canvas');
      const size = 50;
      thumbCanvas.width = size;
      thumbCanvas.height = size;
      const thumbCtx = thumbCanvas.getContext('2d');
      
      // 绘制灰白相间的背景表示透明
      thumbCtx.fillStyle = '#f0f0f0';
      thumbCtx.fillRect(0, 0, size, size);
      
      thumbCtx.fillStyle = '#e0e0e0';
      const squareSize = 5;
      for (let x = 0; x < size; x += squareSize) {
        for (let y = 0; y < size; y += squareSize) {
          if ((x / squareSize + y / squareSize) % 2 === 0) {
            thumbCtx.fillRect(x, y, squareSize, squareSize);
          }
        }
      }
      
      // 绘制缩略图内容
      thumbCtx.drawImage(mergedCanvas, 0, 0, mergedCanvas.width, mergedCanvas.height, 0, 0, size, size);
      
      // 创建新图层数据
      const newLayer = {
        id: newLayerId,
        name: '合并图层',
        image: null, // 没有原始图像
        visible: true,
        opacity: 100,
        blendMode: 'normal',
        filters: {
          blur: 0,
          brightness: 100,
          contrast: 100,
          saturation: 100
        },
        thumbnail: thumbCanvas.toDataURL('image/png'),
        offsetX: minX,  // 保留计算的偏移量
        offsetY: minY
      };
      
      // 保留可见图层的IDs，用于稍后删除
      const visibleLayerIds = visibleLayers.map(layer => layer.id);
      
      // 删除所有可见图层
      const newLayers = this.layers.filter(layer => !layer.visible);
      
      // 添加合并后的图层到数组开头
      newLayers.unshift(newLayer);
      
      // 更新图层数组
      this.layers = newLayers;
      
      // 清理旧图层画布缓存
      visibleLayerIds.forEach(id => {
        delete this.layerCanvases[id];
        delete this.highResLayerCanvases[id];
      });
      
      // 设置活动图层为合并后的图层
      this.activeLayerIndex = 0;
      
      // 重绘画布
      this.redrawCanvas();
    },
    
    // 全选功能
    selectAll() {
      if (this.activeLayerIndex < 0) return;
      
      // 如果已经有选区，则取消选区
      if (this.hasSelection) {
        this.clearSelection();
        return;
      }
      
      const layer = this.layers[this.activeLayerIndex];
      const layerCanvas = this.layerCanvases[layer.id];
      
      if (!layerCanvas) return;
      
      const ctx = layerCanvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, layerCanvas.width, layerCanvas.height);
      const data = imageData.data;
      
      // 创建选区数据（与图层大小相同的布尔数组）
      this.selectionData = new Uint8Array(layerCanvas.width * layerCanvas.height);
      
      // 遍历像素，将非透明像素标记为选中
      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];
        if (alpha > 0) {
          this.selectionData[i / 4] = 1;
        }
      }
      
      this.hasSelection = true;
      this.selectedLayerIndex = this.activeLayerIndex; // 记录选区所属的图层
      
      // 显示选区
      this.showSelection();
    },
    
    // 显示选区
    showSelection() {
      if (!this.hasSelection || !this.selectionData) return;
      
      const layer = this.layers[this.activeLayerIndex];
      const layerCanvas = this.layerCanvases[layer.id];
      
      if (!layerCanvas) return;
      
      // 创建临时画布用于显示选区
      const tempCanvas = this.$refs.tempCanvas;
      const tempCtx = tempCanvas.getContext('2d');
      
      // 清除临时画布
      tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
      
      // 创建一个离屏画布用于绘制原始大小的选区
      const offscreenCanvas = document.createElement('canvas');
      offscreenCanvas.width = layerCanvas.width;
      offscreenCanvas.height = layerCanvas.height;
      const offscreenCtx = offscreenCanvas.getContext('2d');
      
      // 启用抗锯齿
      offscreenCtx.imageSmoothingEnabled = true;
      offscreenCtx.imageSmoothingQuality = 'high';
      
      // 设置选区颜色（淡蓝色，半透明）
      offscreenCtx.fillStyle = 'rgba(33, 150, 243, 0.25)';
      
      // 首先绘制图层内容作为蒙版
      offscreenCtx.drawImage(layerCanvas, 0, 0);
      
      // 使用source-in混合模式，只在原图不透明的地方显示选区
      offscreenCtx.globalCompositeOperation = 'source-in';
      offscreenCtx.fillRect(0, 0, offscreenCanvas.width, offscreenCanvas.height);
      
      // 重置混合模式
      offscreenCtx.globalCompositeOperation = 'source-over';
      
      // 获取当前图层的偏移量
      const offsetX = layer.offsetX || 0;
      const offsetY = layer.offsetY || 0;
      
      // 计算缩放和拖动后的位置
      const zoomFactor = this.zoomLevel / 100;
      const displaySize = this.displayCanvasSize;
      const scaledSize = displaySize * zoomFactor;
      
      // 计算基础偏移（包括缩放和拖动）
      const baseOffsetX = (displaySize - scaledSize) / 2 + this.dragOffsetX;
      const baseOffsetY = (displaySize - scaledSize) / 2 + this.dragOffsetY;
      
      // 计算最终偏移（包括图层自身偏移）
      const finalOffsetX = baseOffsetX + (offsetX * zoomFactor);
      const finalOffsetY = baseOffsetY + (offsetY * zoomFactor);
      
      // 启用临时画布的抗锯齿
      tempCtx.imageSmoothingEnabled = true;
      tempCtx.imageSmoothingQuality = 'high';
      
      // 绘制选区到临时画布，应用所有变换
      tempCtx.drawImage(
        offscreenCanvas,
        0, 0, offscreenCanvas.width, offscreenCanvas.height,
        finalOffsetX, finalOffsetY, scaledSize, scaledSize
      );
      
      // 显示临时画布
      tempCanvas.style.display = 'block';
      tempCanvas.style.position = 'absolute';
      tempCanvas.style.pointerEvents = 'none';
      tempCanvas.style.top = '0';
      tempCanvas.style.left = '0';
      tempCanvas.style.zIndex = '1000';
      
      // 为选区添加边界
      this.drawSelectionBorder(tempCtx, finalOffsetX, finalOffsetY, scaledSize);
    },
    
    // 绘制选区边界
    drawSelectionBorder(tempCtx, offsetX, offsetY, scaledSize) {
      if (!this.hasSelection || !this.selectionData) return;
      
      const layer = this.layers[this.activeLayerIndex];
      const layerCanvas = this.layerCanvases[layer.id];
      
      if (!layerCanvas) return;
      
      // 创建一个离屏画布用于绘制选区边界
      const borderCanvas = document.createElement('canvas');
      borderCanvas.width = layerCanvas.width;
      borderCanvas.height = layerCanvas.height;
      const borderCtx = borderCanvas.getContext('2d');
      
      // 找出选区边界像素
      const width = layerCanvas.width;
      const height = layerCanvas.height;
      
      // 使用更精细的边界检测算法，检查8个方向的邻居
      const dx = [-1, 0, 1, -1, 1, -1, 0, 1];
      const dy = [-1, -1, -1, 0, 0, 1, 1, 1];
      
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const index = y * width + x;
          if (this.selectionData[index]) {
            let isBorder = false;
            
            // 检查8个方向的邻居
            for (let d = 0; d < 8; d++) {
              const nx = x + dx[d];
              const ny = y + dy[d];
              
              // 检查边界条件
              if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
                const neighborIndex = ny * width + nx;
                // 如果有一个邻居不在选区内，则当前像素是边界
                if (!this.selectionData[neighborIndex]) {
                  isBorder = true;
                  break;
                }
              } else {
                // 如果邻居在画布外，当前像素也是边界
                isBorder = true;
                break;
              }
            }
            
            if (isBorder) {
              borderCtx.fillStyle = '#ffffff';
              borderCtx.fillRect(x, y, 1, 1);
            }
          }
        }
      }
      
      // 为边界添加柔和效果以增强平滑度
      borderCtx.filter = 'blur(0.5px)';
      borderCtx.drawImage(borderCanvas, 0, 0);
      borderCtx.filter = 'none';
      
      // 设置虚线边界样式
      tempCtx.strokeStyle = '#00a8ff';
      tempCtx.lineWidth = 1.5;
      tempCtx.setLineDash([4, 3]);
      
      // 绘制边界到临时画布
      tempCtx.drawImage(
        borderCanvas,
        0, 0, borderCanvas.width, borderCanvas.height,
        offsetX, offsetY, scaledSize, scaledSize
      );
    },
    
    // 清除选区
    clearSelection() {
      this.hasSelection = false;
      this.selectionData = null;
      this.selectedLayerIndex = -1; // 清除选区所属图层的记录
      
      // 隐藏临时画布
      const tempCanvas = this.$refs.tempCanvas;
      tempCanvas.style.display = 'none';
      
      // 清除临时画布内容
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
      
      // 重绘画布以更新显示
      this.redrawCanvas();
    },
    
    // 触摸事件处理
    handleTouchStart(e) {
      // 如果当前是拖动模式，交给startDrag处理
      if (this.zoomLevel > 100 && !this.currentTool.active) {
        this.startDrag(e);
        return;
      }
      
      e.preventDefault(); // 阻止默认行为，如页面滚动
      
      if (!this.imageLoaded || this.activeLayerIndex < 0 || !this.currentTool.active) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      this.isDrawing = true;
      
      const touch = e.touches[0];
      const rect = this.$refs.canvas.getBoundingClientRect();
      
      // 计算准确的触摸坐标，考虑设备像素比、画布缩放和用户设置的缩放
      const scaleX = this.$refs.canvas.width / rect.width;
      const scaleY = this.$refs.canvas.height / rect.height;
      const zoomFactor = this.zoomLevel / 100;
      
      // 调整坐标以适应缩放和拖动
      const canvasCenterX = rect.width / 2;
      const canvasCenterY = rect.height / 2;
      const relativeX = (touch.clientX - rect.left - canvasCenterX) / zoomFactor + canvasCenterX - this.dragOffsetX / zoomFactor;
      const relativeY = (touch.clientY - rect.top - canvasCenterY) / zoomFactor + canvasCenterY - this.dragOffsetY / zoomFactor;
      
      this.lastX = relativeX * scaleX;
      this.lastY = relativeY * scaleY;
    },
    
    handleTouchMove(e) {
      // 如果当前是拖动模式，交给doDrag处理
      if (this.isDragging) {
        this.doDrag(e);
        return;
      }
      
      e.preventDefault(); // 阻止默认行为，如页面滚动
      
      if (!this.isDrawing) return;
      
      const touch = e.touches[0];
      const rect = this.$refs.canvas.getBoundingClientRect();
      
      // 计算准确的触摸坐标，考虑设备像素比、画布缩放和用户设置的缩放
      const scaleX = this.$refs.canvas.width / rect.width;
      const scaleY = this.$refs.canvas.height / rect.height;
      const zoomFactor = this.zoomLevel / 100;
      
      // 调整坐标以适应缩放和拖动
      const canvasCenterX = rect.width / 2;
      const canvasCenterY = rect.height / 2;
      const relativeX = (touch.clientX - rect.left - canvasCenterX) / zoomFactor + canvasCenterX - this.dragOffsetX / zoomFactor;
      const relativeY = (touch.clientY - rect.top - canvasCenterY) / zoomFactor + canvasCenterY - this.dragOffsetY / zoomFactor;
      
      const x = relativeX * scaleX;
      const y = relativeY * scaleY;
      
      this.performDrawing(x, y);
    },
    
    // 图像拖动相关方法
    startDrag(e) {
      // 只有当图像被放大时才允许拖动
      if (this.zoomLevel <= 100 || !this.imageLoaded) return;
      
      // 如果当前正在绘图，不允许拖动
      if (this.currentTool.active) return;
      
      this.isDragging = true;
      
      // 记录拖动起始位置
      if (e.type === 'mousedown') {
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;
      } else if (e.type === 'touchstart') {
        const touch = e.touches[0];
        this.dragStartX = touch.clientX;
        this.dragStartY = touch.clientY;
      }
    },
    
    doDrag(e) {
      if (!this.isDragging) return;
      
      let currentX, currentY;
      
      if (e.type === 'mousemove') {
        currentX = e.clientX;
        currentY = e.clientY;
      } else if (e.type === 'touchmove') {
        const touch = e.touches[0];
        currentX = touch.clientX;
        currentY = touch.clientY;
        // 阻止触摸事件的默认行为（如页面滚动）
        e.preventDefault();
      }
      
      // 计算拖动的距离
      const deltaX = currentX - this.dragStartX;
      const deltaY = currentY - this.dragStartY;
      
      // 更新拖动偏移量
      this.dragOffsetX += deltaX;
      this.dragOffsetY += deltaY;
      
      // 限制拖动范围，防止图像拖出可视区域太多
      const maxOffset = (this.zoomLevel / 100 - 1) * this.displayCanvasSize / 2;
      this.dragOffsetX = Math.max(-maxOffset, Math.min(maxOffset, this.dragOffsetX));
      this.dragOffsetY = Math.max(-maxOffset, Math.min(maxOffset, this.dragOffsetY));
      
      // 更新起始位置
      this.dragStartX = currentX;
      this.dragStartY = currentY;
      
      // 重绘画布
      this.redrawCanvas();
    },
    
    endDrag() {
      this.isDragging = false;
    },
    
    // 图层移动控制
    toggleLayerMoveControls() {
      this.showLayerMoveControls = !this.showLayerMoveControls;
    },
    
    moveLayerPosition(dx, dy) {
      if (this.activeLayerIndex < 0 || this.activeLayerIndex >= this.layers.length) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      // 定义移动步长（像素）
      const moveStep = 1;
      
      // 获取当前图层
      const layer = this.layers[this.activeLayerIndex];
      
      // 记录旧的偏移量
      const oldOffsetX = layer.offsetX || 0;
      const oldOffsetY = layer.offsetY || 0;

      // 更新图层位置
      layer.offsetX = oldOffsetX + (dx * moveStep);
      layer.offsetY = oldOffsetY + (dy * moveStep);

      // 记录动作（旧方式，向后兼容）
      this.recordAction('moveLayerPosition', {
        layerIndex: this.activeLayerIndex,
        oldOffsetX: oldOffsetX,
        oldOffsetY: oldOffsetY,
        newOffsetX: layer.offsetX,
        newOffsetY: layer.offsetY
      });

      // 重绘画布
      this.redrawCanvas();

      // 捕获状态快照
      if (this.isRecording) {
        this.captureSnapshot(`移动图层 ${layer.name} 位置: (${layer.offsetX}, ${layer.offsetY})`);
      }
    },
    
    resetLayerPosition() {
      if (this.activeLayerIndex < 0 || this.activeLayerIndex >= this.layers.length) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      // 获取当前图层
      const layer = this.layers[this.activeLayerIndex];
      
      // 记录旧的偏移量
      const oldOffsetX = layer.offsetX || 0;
      const oldOffsetY = layer.offsetY || 0;

      // 重置图层位置
      layer.offsetX = 0;
      layer.offsetY = 0;

      // 记录动作（旧方式，向后兼容）
      this.recordAction('moveLayerPosition', {
        layerIndex: this.activeLayerIndex,
        oldOffsetX: oldOffsetX,
        oldOffsetY: oldOffsetY,
        newOffsetX: 0,
        newOffsetY: 0
      });

      // 重绘画布
      this.redrawCanvas();

      // 捕获状态快照
      if (this.isRecording) {
        this.captureSnapshot(`重置图层 ${layer.name} 位置`);
      }
    },
    
    // 填充图层选项
    openFillLayerOptions() {
      this.showFillLayerOptions = true;
    },
    
    // 处理填充图片上传
    handleFillImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // 创建预览URL
      this.fillLayerOptions.previewUrl = URL.createObjectURL(file);
      
      // 加载图片
      const img = new Image();
      img.onload = () => {
        this.fillLayerOptions.image = img;
      };
      img.src = this.fillLayerOptions.previewUrl;
    },
    
    // 修改填充图层方法
    applyFillLayer() {
      if (this.activeLayerIndex < 0) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      const layer = this.layers[this.activeLayerIndex];
      const layerCanvas = this.layerCanvases[layer.id];
      
      if (!layerCanvas) return;
      
      try {
        // 创建临时画布用于保存原始图像
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = layerCanvas.width;
        tempCanvas.height = layerCanvas.height;
        const tempCtx = tempCanvas.getContext('2d');
        
        // 复制原始图层到临时画布
        tempCtx.drawImage(layerCanvas, 0, 0);
        
        // 获取原始图层的上下文
        const ctx = layerCanvas.getContext('2d');
        
        // 清除原图层内容
        ctx.clearRect(0, 0, layerCanvas.width, layerCanvas.height);
        
        if (this.fillLayerOptions.type === 'color') {
          // 纯色填充
          ctx.fillStyle = this.fillLayerOptions.color;
          ctx.fillRect(0, 0, layerCanvas.width, layerCanvas.height);
        } else if (this.fillLayerOptions.type === 'image' && this.fillLayerOptions.image) {
          // 图片填充 - 创建平铺图案
          const pattern = ctx.createPattern(this.fillLayerOptions.image, 'repeat');
          ctx.fillStyle = pattern;
          ctx.fillRect(0, 0, layerCanvas.width, layerCanvas.height);
        }
        
        // 设置混合模式为destination-in
        ctx.globalCompositeOperation = 'destination-in';
        
        // 绘制原始图像作为蒙版
        ctx.drawImage(tempCanvas, 0, 0);
        
        // 重置混合模式
        ctx.globalCompositeOperation = 'source-over';
        
        console.log('成功应用图层填充');
        
        // 清理预览URL
        if (this.fillLayerOptions.previewUrl) {
          URL.revokeObjectURL(this.fillLayerOptions.previewUrl);
          this.fillLayerOptions.previewUrl = null;
        }
      } catch (error) {
        console.error('应用图层填充时出错:', error);
      }
      
      // 同步到高分辨率画布
      this.createHighResCanvasFromDisplay(layer.id);
      
      // 更新缩略图
      this.updateLayerThumbnail(this.activeLayerIndex);
      
      // 重绘主画布
      this.redrawCanvas();
      
      // 关闭填充图层选项面板
      this.showFillLayerOptions = false;
    },
    
    goToWebVersion() {
      this.router.push('/web');
    },
    
    // 打开填充选项
    openFillOptions() {
      if (!this.hasSelection) return;
      this.showFillOptions = true;
    },
    
    // 应用填充
    applyFill() {
      if (!this.hasSelection || !this.selectionData) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      const layer = this.layers[this.activeLayerIndex];
      const layerCanvas = this.layerCanvases[layer.id];
      
      if (!layerCanvas) return;
      
      const ctx = layerCanvas.getContext('2d');
      
      // 创建临时画布用于保存原始内容
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = layerCanvas.width;
      tempCanvas.height = layerCanvas.height;
      const tempCtx = tempCanvas.getContext('2d');
      
      // 保存原始图层内容
      tempCtx.drawImage(layerCanvas, 0, 0);
      
      // 创建选区蒙版画布
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = layerCanvas.width;
      maskCanvas.height = layerCanvas.height;
      const maskCtx = maskCanvas.getContext('2d');
      
      // 创建选区形状
      const imageData = ctx.getImageData(0, 0, layerCanvas.width, layerCanvas.height);
      const data = imageData.data;
      
      // 将选区数据转换为ImageData
      const maskImageData = new ImageData(layerCanvas.width, layerCanvas.height);
      const maskData = maskImageData.data;
      
      for (let i = 0; i < this.selectionData.length; i++) {
        if (this.selectionData[i]) {
          const pixelIndex = i * 4;
          // 只在原图不透明的地方创建选区
          if (data[pixelIndex + 3] > 0) {
            maskData[pixelIndex] = 255;
            maskData[pixelIndex + 1] = 255;
            maskData[pixelIndex + 2] = 255;
            maskData[pixelIndex + 3] = 255;
          }
        }
      }
      
      // 将选区蒙版绘制到蒙版画布
      maskCtx.putImageData(maskImageData, 0, 0);
      
      // 清空原画布
      ctx.clearRect(0, 0, layerCanvas.width, layerCanvas.height);
      
      // 创建填充画布
      const fillCanvas = document.createElement('canvas');
      fillCanvas.width = layerCanvas.width;
      fillCanvas.height = layerCanvas.height;
      const fillCtx = fillCanvas.getContext('2d');
      
      // 在填充画布上绘制选区形状
      fillCtx.drawImage(maskCanvas, 0, 0);
      
      // 使用选区作为蒙版进行填充
      fillCtx.globalCompositeOperation = 'source-in';
      fillCtx.fillStyle = this.fillOptions.color;
      fillCtx.fillRect(0, 0, fillCanvas.width, fillCanvas.height);
      
      // 恢复原始内容
      ctx.drawImage(tempCanvas, 0, 0);
      
      // 在原始内容上叠加填充
      ctx.globalCompositeOperation = 'destination-out';
      ctx.drawImage(maskCanvas, 0, 0);
      
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(fillCanvas, 0, 0);
      
      // 同步到高分辨率画布
      this.createHighResCanvasFromDisplay(layer.id);
      
      // 更新缩略图
      this.updateLayerThumbnail(this.activeLayerIndex);
      
      // 清除选区
      this.clearSelection();
      
      // 重绘主画布
      this.redrawCanvas();
      
      // 关闭填充选项面板
      this.showFillOptions = false;
    },
    
    // 删除选区内容
    deleteSelection() {
      if (!this.hasSelection || this.activeLayerIndex < 0) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      const layer = this.layers[this.activeLayerIndex];
      const layerCanvas = this.layerCanvases[layer.id];
      
      if (!layerCanvas) return;
      
      const ctx = layerCanvas.getContext('2d');
      
      // 创建一个离屏画布用于处理
      const offscreenCanvas = document.createElement('canvas');
      offscreenCanvas.width = layerCanvas.width;
      offscreenCanvas.height = layerCanvas.height;
      const offscreenCtx = offscreenCanvas.getContext('2d');
      
      // 启用抗锯齿
      offscreenCtx.imageSmoothingEnabled = true;
      offscreenCtx.imageSmoothingQuality = 'high';
      
      // 复制原始内容
      offscreenCtx.drawImage(layerCanvas, 0, 0);
      
      // 创建选区蒙版
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = layerCanvas.width;
      maskCanvas.height = layerCanvas.height;
      const maskCtx = maskCanvas.getContext('2d');
      
      // 启用抗锯齿
      maskCtx.imageSmoothingEnabled = true;
      maskCtx.imageSmoothingQuality = 'high';
      
      // 获取图层的图像数据
      const imageData = offscreenCtx.getImageData(0, 0, offscreenCanvas.width, offscreenCanvas.height);
      const data = imageData.data;
      
      // 将选区数据转换为ImageData，使用alpha通道创建平滑边缘
      const maskImageData = new ImageData(offscreenCanvas.width, offscreenCanvas.height);
      const maskData = maskImageData.data;
      
      for (let i = 0; i < this.selectionData.length; i++) {
        if (this.selectionData[i]) {
          const pixelIndex = i * 4;
          const alpha = data[pixelIndex + 3];
          if (alpha > 0) {
            maskData[pixelIndex] = 255;
            maskData[pixelIndex + 1] = 255;
            maskData[pixelIndex + 2] = 255;
            maskData[pixelIndex + 3] = alpha; // 使用原始alpha值
          }
        }
      }
      
      // 将选区蒙版绘制到蒙版画布
      maskCtx.putImageData(maskImageData, 0, 0);
      
      // 清空原画布
      ctx.clearRect(0, 0, layerCanvas.width, layerCanvas.height);
      
      // 使用选区作为蒙版保留非选区内容
      ctx.save();
      ctx.drawImage(offscreenCanvas, 0, 0);
      ctx.globalCompositeOperation = 'destination-out';
      ctx.drawImage(maskCanvas, 0, 0);
      ctx.restore();
      
      // 同步到高分辨率画布
      this.createHighResCanvasFromDisplay(layer.id);
      
      // 更新缩略图
      this.updateLayerThumbnail(this.activeLayerIndex);
      
      // 清除选区
      this.clearSelection();
      
      // 重绘主画布
      this.redrawCanvas();
    },
    
    // 复制选区内容
    copySelection() {
      if (!this.hasSelection || this.activeLayerIndex < 0) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      const layer = this.layers[this.activeLayerIndex];
      const layerCanvas = this.layerCanvases[layer.id];
      
      if (!layerCanvas) return;
      
      // 创建新的图层ID
      const newLayerId = this.nextLayerId++;
      
      // 获取主画布上的实际渲染结果，包含所有应用效果
      const mainCanvas = this.$refs.canvas;
      
      // 创建一个临时画布用于捕获应用了所有效果的图层内容
      const effectsCanvas = document.createElement('canvas');
      effectsCanvas.width = layerCanvas.width;
      effectsCanvas.height = layerCanvas.height;
      const effectsCtx = effectsCanvas.getContext('2d');
      
      // 启用抗锯齿
      effectsCtx.imageSmoothingEnabled = true;
      effectsCtx.imageSmoothingQuality = 'high';
      
      // 保存当前活动图层的可见状态
      const originalVisibility = [];
      for (let i = 0; i < this.layers.length; i++) {
        originalVisibility.push(this.layers[i].visible);
        // 暂时隐藏所有其他图层
        this.layers[i].visible = (i === this.activeLayerIndex);
      }
      
      // 临时重绘画布以获取仅含当前图层的渲染结果
      this.redrawCanvas();
      
      // 获取图层位置和缩放信息
      const zoomFactor = this.zoomLevel / 100;
      const displaySize = this.displayCanvasSize;
      const offsetX = layer.offsetX || 0;
      const offsetY = layer.offsetY || 0;
      
      // 计算基础偏移（包括缩放和拖动）
      const baseOffsetX = (displaySize - displaySize * zoomFactor) / 2 + this.dragOffsetX;
      const baseOffsetY = (displaySize - displaySize * zoomFactor) / 2 + this.dragOffsetY;
      
      // 计算最终偏移（包括图层自身偏移）
      const finalOffsetX = baseOffsetX + (offsetX * zoomFactor);
      const finalOffsetY = baseOffsetY + (offsetY * zoomFactor);
      
      // 从主画布复制渲染后的图层（带有应用的效果）
      effectsCtx.drawImage(
        mainCanvas,
        finalOffsetX, finalOffsetY, displaySize * zoomFactor, displaySize * zoomFactor,
        0, 0, layerCanvas.width, layerCanvas.height
      );
      
      // 恢复图层可见性
      for (let i = 0; i < this.layers.length; i++) {
        this.layers[i].visible = originalVisibility[i];
      }
      
      // 重绘画布恢复原始显示状态
      this.redrawCanvas();
      
      // 创建选区蒙版
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = layerCanvas.width;
      maskCanvas.height = layerCanvas.height;
      const maskCtx = maskCanvas.getContext('2d');
      
      // 启用抗锯齿
      maskCtx.imageSmoothingEnabled = true;
      maskCtx.imageSmoothingQuality = 'high';
      
      // 获取图层的图像数据
      const imageData = layerCanvas.getContext('2d').getImageData(0, 0, layerCanvas.width, layerCanvas.height);
      const data = imageData.data;
      
      // 将选区数据转换为ImageData，使用alpha通道创建平滑边缘
      const maskImageData = new ImageData(layerCanvas.width, layerCanvas.height);
      const maskData = maskImageData.data;
      
      // 创建2像素的边缘平滑过渡
      const width = layerCanvas.width;
      for (let i = 0; i < this.selectionData.length; i++) {
        if (this.selectionData[i]) {
          const x = i % width;
          const y = Math.floor(i / width);
          const pixelIndex = i * 4;
          const alpha = data[pixelIndex + 3];
          
          if (alpha > 0) {
            // 检查周围8个像素
            let edgeCount = 0;
            for (let dy = -1; dy <= 1; dy++) {
              for (let dx = -1; dx <= 1; dx++) {
                if (dx === 0 && dy === 0) continue;
                
                const nx = x + dx;
                const ny = y + dy;
                if (nx >= 0 && nx < width && ny >= 0 && ny < width) {
                  const ni = ny * width + nx;
                  if (!this.selectionData[ni]) {
                    edgeCount++;
                  }
                }
              }
            }
            
            // 根据边缘像素数量设置alpha值
            let finalAlpha = alpha;
            if (edgeCount > 0) {
              finalAlpha = Math.floor(alpha * (1 - edgeCount / 16)); // 创建平滑过渡
            }
            
            maskData[pixelIndex] = 255;
            maskData[pixelIndex + 1] = 255;
            maskData[pixelIndex + 2] = 255;
            maskData[pixelIndex + 3] = finalAlpha;
          }
        }
      }
      
      // 将选区蒙版绘制到蒙版画布
      maskCtx.putImageData(maskImageData, 0, 0);
      
      // 创建新画布
      const newCanvas = document.createElement('canvas');
      newCanvas.width = layerCanvas.width;
      newCanvas.height = layerCanvas.height;
      const newCtx = newCanvas.getContext('2d');
      
      // 启用抗锯齿
      newCtx.imageSmoothingEnabled = true;
      newCtx.imageSmoothingQuality = 'high';
      
      // 复制带效果的内容
      newCtx.drawImage(effectsCanvas, 0, 0);
      
      // 使用选区作为蒙版
      newCtx.globalCompositeOperation = 'destination-in';
      newCtx.drawImage(maskCanvas, 0, 0);
      
      // 存储新画布
      this.layerCanvases[newLayerId] = newCanvas;
      
      // 创建高分辨率版本
      this.createHighResCanvasFromDisplay(newLayerId);
      
      // 创建新图层数据
      const newLayer = {
        id: newLayerId,
        name: `${layer.name} 效果选区`,
        image: null,
        visible: true,
        opacity: 100,
        blendMode: 'normal',
        filters: {
          blur: 0,
          brightness: 100,
          contrast: 100,
          saturation: 100
        },
        offsetX: layer.offsetX || 0,
        offsetY: layer.offsetY || 0
      };
      
      // 创建缩略图
      const thumbCanvas = document.createElement('canvas');
      const size = 50;
      thumbCanvas.width = size;
      thumbCanvas.height = size;
      const thumbCtx = thumbCanvas.getContext('2d');
      
      // 启用抗锯齿
      thumbCtx.imageSmoothingEnabled = true;
      thumbCtx.imageSmoothingQuality = 'high';
      
      // 绘制灰白相间的背景表示透明
      thumbCtx.fillStyle = '#f0f0f0';
      thumbCtx.fillRect(0, 0, size, size);
      
      thumbCtx.fillStyle = '#e0e0e0';
      const squareSize = 5;
      for (let x = 0; x < size; x += squareSize) {
        for (let y = 0; y < size; y += squareSize) {
          if ((x / squareSize + y / squareSize) % 2 === 0) {
            thumbCtx.fillRect(x, y, squareSize, squareSize);
          }
        }
      }
      
      // 绘制缩略图内容
      thumbCtx.drawImage(newCanvas, 0, 0, newCanvas.width, newCanvas.height, 0, 0, size, size);
      newLayer.thumbnail = thumbCanvas.toDataURL('image/png');
      
      // 在当前图层之上插入新图层
      this.layers.splice(this.activeLayerIndex, 0, newLayer);
      
      // 选中新创建的图层
      this.activeLayerIndex = this.activeLayerIndex - 1;
      
      // 重绘画布
      this.redrawCanvas();
    },
    
    // 打开放大选项
    openScaleOptions() {
      if (this.activeLayerIndex < 0) return;
      this.showScaleOptions = true;
      this.scaleOptions.scale = 100;
    },
    
    // 应用放大
    applyScale() {
      if (this.activeLayerIndex < 0) return;
      
      // 保存当前状态
      this.saveCanvasState();
      
      const layer = this.layers[this.activeLayerIndex];
      const layerCanvas = this.layerCanvases[layer.id];
      
      if (!layerCanvas) return;
      
      // 计算新的尺寸
      const scale = this.scaleOptions.scale / 100;
      const newWidth = Math.round(layerCanvas.width * scale);
      const newHeight = Math.round(layerCanvas.height * scale);
      
      // 创建新画布
      const newCanvas = document.createElement('canvas');
      newCanvas.width = this.displayCanvasSize;
      newCanvas.height = this.displayCanvasSize;
      const newCtx = newCanvas.getContext('2d');
      
      // 启用抗锯齿
      newCtx.imageSmoothingEnabled = true;
      newCtx.imageSmoothingQuality = 'high';
      
      // 计算居中位置
      const x = (this.displayCanvasSize - newWidth) / 2;
      const y = (this.displayCanvasSize - newHeight) / 2;
      
      // 创建临时画布用于放大
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = newWidth;
      tempCanvas.height = newHeight;
      const tempCtx = tempCanvas.getContext('2d');
      
      // 启用抗锯齿
      tempCtx.imageSmoothingEnabled = true;
      tempCtx.imageSmoothingQuality = 'high';
      
      // 放大图像
      tempCtx.drawImage(
        layerCanvas,
        0, 0, layerCanvas.width, layerCanvas.height,
        0, 0, newWidth, newHeight
      );
      
      // 将放大后的图像绘制到新画布的中心
      newCtx.drawImage(tempCanvas, x, y);
      
      // 更新图层画布
      this.layerCanvases[layer.id] = newCanvas;
      
      // 更新图层偏移量
      layer.offsetX = (layer.offsetX || 0) * scale;
      layer.offsetY = (layer.offsetY || 0) * scale;
      
      // 创建高分辨率版本
      this.createHighResCanvasFromDisplay(layer.id);
      
      // 更新缩略图
      this.updateLayerThumbnail(this.activeLayerIndex);
      
      // 重绘画布
      this.redrawCanvas();
      
      // 关闭放大选项面板
      this.showScaleOptions = false;
    },
    
    // 录制动作相关方法
    startRecording() {
      if (this.isRecording) {
        // 结束录制
        if (this.actionSnapshots.length === 0) {
          alert('没有记录到任何动作，录制已取消');
          this.isRecording = false;
          return;
        }
        
        // 提示用户输入动作名称
        const name = prompt('请输入动作名称:', '新动作');
        if (!name) {
          alert('录制已取消');
          this.isRecording = false;
          this.actionSnapshots = [];
          return;
        }
        
        this.currentRecordName = name;
        this.saveRecordedActions();
      } else {
        // 开始录制
        this.isRecording = true;
        this.actionSnapshots = [];
        
        // 记录初始状态
        this.captureSnapshot('初始状态');
        
        alert('开始录制动作，所有的图层操作都将被记录');
      }
    },
    
    // 捕获当前状态快照
    captureSnapshot(actionName) {
      if (!this.isRecording) return;
      
      // 创建画布状态的深度副本
      const canvasState = {
        name: actionName,
        timestamp: Date.now(),
        layers: JSON.parse(JSON.stringify(this.layers)), // 深拷贝图层元数据
        canvasData: {}, // 存储每个图层的画布数据
        activeLayerIndex: this.activeLayerIndex,
        zoomLevel: this.zoomLevel,
        dragOffsetX: this.dragOffsetX,
        dragOffsetY: this.dragOffsetY
      };
      
      // 遍历所有图层，保存其画布数据
      for (const layer of this.layers) {
        const canvas = this.layerCanvases[layer.id];
        if (canvas) {
          canvasState.canvasData[layer.id] = canvas.toDataURL('image/png');
        }
      }
      
      // 添加到快照列表
      this.actionSnapshots.push(canvasState);
      
      console.log(`已记录操作: ${actionName}`);
    },
    
    // 保存录制的动作
    async saveRecordedActions() {
      try {
        const actionData = {
          name: this.currentRecordName,
          version: '2.0', // 版本号，区分新旧格式
          snapshots: this.actionSnapshots,
          createdAt: new Date().toISOString()
        };
        
        // 将动作数据转换为JSON字符串
        const actionJson = JSON.stringify(actionData, null, 2);
        
        // 创建Blob对象
        const blob = new Blob([actionJson], { type: 'application/json' });
        
        // 创建下载链接
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.currentRecordName.replace(/\s+/g, '_')}_action.json`;
        
        // 触发下载
        document.body.appendChild(link);
        link.click();
        
        // 清理
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // 重置状态
        this.isRecording = false;
        this.actionSnapshots = [];
        const actionName = this.currentRecordName; // 保存动作名称供后续使用
        this.currentRecordName = '';
        
        // 如果是管理员，同时上传到服务器
        if (this.isAdmin) {
          await this.uploadActionToServer(actionName, actionData);
        } else {
          // 只有在不是管理员（不会上传到服务器）时才显示这个提示
          alert('动作已保存到本地');
        }
      } catch (error) {
        console.error('保存动作时出错:', error);
        alert('保存动作失败: ' + error.message);
      }
    },
    
    // 上传动作文件到服务器
    async uploadActionToServer(actionName, actionData) {
      try {
        console.log('正在上传动作文件到服务器...');
        const response = await api.uploadAction({
          action_name: actionName,
          action_data: actionData
        });
        
        if (response.success) {
          console.log('动作文件上传成功:', response.data);
          console.log('服务器路径:', response.data.file_path);
          // 显示上传成功的提示
          alert(`动作已保存到本地并上传至服务器\n文件名: ${response.data.file_name}`);
        } else {
          throw new Error(response.message);
        }
      } catch (error) {
        console.error('上传动作文件失败:', error);
        // 这里我们选择不中断用户操作，只记录错误
        // 用户仍然有本地文件可用
        alert('动作已保存到本地，但上传到服务器失败');
      }
    },
    
    // 动作管理相关方法
    async toggleActionManager() {
      if (this.isRecording || this.isPlayingAction) return;
      
      this.showActionManager = !this.showActionManager;
      
      // 如果是打开管理器,则自动加载动作文件
      if (this.showActionManager) {
        try {
          const response = await api.uploadAction(); // 发送GET请求获取动作文件列表
          if (response.success) {
            // 直接使用返回的动作数据
            this.loadedActions = response.data;
            if (this.loadedActions.length === 0) {
              console.log('没有找到可用的动作文件');
            } else {
              console.log(`成功加载 ${this.loadedActions.length} 个动作`);
            }
          } else {
            console.warn('加载动作文件失败:', response.message);
          }
        } catch (error) {
          // 如果错误中包含响应数据，尝试使用它
          if (error.response && error.response.data && error.response.data.success) {
            this.loadedActions = error.response.data.data;
            console.warn('请求超时，但成功获取到动作数据');
            return;
          }
          
          // 其他错误情况
          console.error('获取动作文件出错:', error);
          if (error.code === 'ECONNABORTED') {
            console.warn('请求超时，请检查网络连接');
          }
        }
      }
    },
    
    handleActionFileUpload(event) {
      const files = event.target.files;
      if (!files.length) return;
      
      const file = files[0];
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const actionData = JSON.parse(e.target.result);
          
          // 验证动作数据格式 - 同时支持新旧两种格式
          // 新格式: 有name和snapshots
          // 旧格式: 有name和actions
          if (!actionData.name || 
              (!Array.isArray(actionData.snapshots) && !Array.isArray(actionData.actions))) {
            throw new Error('无效的动作文件格式，缺少必要字段');
          }
          
          // 为旧格式文件添加版本标识
          if (!actionData.version && Array.isArray(actionData.actions)) {
            actionData.version = '1.0'; // 标记为旧版本
            console.log('检测到旧版动作文件格式，已添加版本标识');
          }
          
          // 添加到已加载的动作列表
          this.loadedActions.push(actionData);
          
          // 区分显示的消息
          if (actionData.version === '1.0') {
            alert(`旧版动作"${actionData.name}"已加载 (兼容模式)`);
          } else {
            alert(`动作"${actionData.name}"已加载`);
          }
        } catch (error) {
          console.error('加载动作文件失败:', error);
          alert('加载动作文件失败: ' + error.message);
        }
      };
      
      reader.readAsText(file);
      event.target.value = ''; // 清空input以允许重复选择相同文件
    },
    
    async playAction(index) {
      if (this.isPlayingAction || this.isRecording) return;

      const actionData = this.loadedActions[index];
      if (!actionData) return;

      // 提示用户动作将应用于当前图像
      if (!confirm(`动作"${actionData.name}"将应用于当前图像。继续吗？`)) {
        return;
      }

      this.isPlayingAction = true;
      this.currentActionIndex = index;

      try {
        // 判断动作格式版本
        if (actionData.version === '2.0' && actionData.snapshots) {
          // 新版格式：基于快照的动作执行
          await this.playActionFromSnapshots(actionData.snapshots);
        } else if (actionData.version === '1.0' && actionData.actions) {
          // 旧版格式：基于操作序列的动作执行
          await this.playLegacyAction(actionData.actions);
        } else {
          throw new Error('不支持的动作格式');
        }

        alert(`动作"${actionData.name}"执行完成`);
      } catch (error) {
        console.error('执行动作时出错:', error);
        alert('执行动作失败: ' + error.message);
      } finally {
        this.isPlayingAction = false;
        this.currentActionIndex = -1;
      }
    },
    
    // 从状态快照执行动作（新版方法）
    async playActionFromSnapshots(snapshots) {
      if (!snapshots || !snapshots.length) return;
      
      // 保存当前状态以便撤销
      this.saveCanvasState();
      
      try {
        // 遍历每个快照中的操作
        for (let i = 1; i < snapshots.length; i++) {
          const snapshot = snapshots[i];
          const prevSnapshot = snapshots[i - 1];
          
          // 分析操作类型
          const operation = this.analyzeOperation(prevSnapshot, snapshot);
          console.log(`检测到操作: ${operation.type}`);
          
          // 执行相应的操作
          await this.executeOperation(operation);
          
          // 添加小延迟以便观察动作执行过程
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } catch (error) {
        console.error('执行动作序列时出错:', error);
        throw error;
      }
    },
    
    // 分析两个快照之间的操作差异
    analyzeOperation(prevSnapshot, currentSnapshot) {
      // 默认操作对象
      const operation = {
        type: 'unknown',
        params: {}
      };
      
      try {
        // 检查图层数量变化
        const prevLayers = prevSnapshot.layers;
        const currentLayers = currentSnapshot.layers;
        
        // 检查是否有图层被删除
        if (currentLayers.length < prevLayers.length) {
          operation.type = 'deleteLayer';
          operation.params.layerIndex = this.findDeletedLayerIndex(prevLayers, currentLayers);
          return operation;
        }
        
        // 检查是否有新图层添加（复制）
        if (currentLayers.length > prevLayers.length) {
          operation.type = 'duplicateLayer';
          operation.params.layerIndex = this.findDuplicatedLayerIndex(prevLayers, currentLayers);
          return operation;
        }
        
        // 检查图层顺序变化
        const orderChanged = this.checkLayerOrderChange(prevLayers, currentLayers);
        if (orderChanged.changed) {
          operation.type = orderChanged.type; // 'moveLayerUp' 或 'moveLayerDown'
          operation.params.layerIndex = orderChanged.index;
          return operation;
        }
        
        // 检查图层属性变化
        for (let i = 0; i < currentLayers.length; i++) {
          const prevLayer = prevLayers[i];
          const currentLayer = currentLayers[i];
          
          // 检查混合模式变化
          if (prevLayer.blendMode !== currentLayer.blendMode) {
            operation.type = 'updateBlendMode';
            operation.params = {
              layerIndex: i,
              blendMode: currentLayer.blendMode
            };
            return operation;
          }
          
          // 检查不透明度变化
          if (prevLayer.opacity !== currentLayer.opacity) {
            operation.type = 'updateLayerOpacity';
            operation.params = {
              layerIndex: i,
              opacity: currentLayer.opacity
            };
            return operation;
          }
          
          // 检查滤镜变化
          if (JSON.stringify(prevLayer.filters) !== JSON.stringify(currentLayer.filters)) {
            operation.type = 'updateFilters';
            operation.params = {
              layerIndex: i,
              filters: { ...currentLayer.filters }
            };
            return operation;
          }
          
          // 检查位置变化
          if (prevLayer.offsetX !== currentLayer.offsetX || 
              prevLayer.offsetY !== currentLayer.offsetY) {
            operation.type = 'moveLayerPosition';
            operation.params = {
              layerIndex: i,
              newOffsetX: currentLayer.offsetX || 0,
              newOffsetY: currentLayer.offsetY || 0
            };
            return operation;
          }
          
          // 检查可见性变化
          if (prevLayer.visible !== currentLayer.visible) {
            operation.type = 'toggleLayerVisibility';
            operation.params = {
              layerIndex: i
            };
            return operation;
          }
          
          // 检查名称变化
          if (prevLayer.name !== currentLayer.name) {
            operation.type = 'renameLayer';
            operation.params = {
              layerIndex: i,
              oldName: prevLayer.name,
              newName: currentLayer.name
            };
            return operation;
          }
        }
        
        // 如果没有检测到具体变化，可能是复杂操作（如反相）
        if (currentSnapshot.name.includes('反相')) {
          operation.type = 'invertLayer';
          operation.params = {
            layerIndex: this.findModifiedLayerIndex(prevLayers, currentLayers)
          };
          return operation;
        }
        
      } catch (error) {
        console.error('分析操作时出错:', error);
      }
      
      return operation;
    },
    
    // 查找被删除的图层索引
    findDeletedLayerIndex(prevLayers, currentLayers) {
      for (let i = 0; i < prevLayers.length; i++) {
        if (!currentLayers.find(l => l.id === prevLayers[i].id)) {
          return i;
        }
      }
      return -1;
    },
    
    // 查找复制的图层索引
    findDuplicatedLayerIndex(prevLayers, currentLayers) {
      // 通常复制的图层会被插入到原图层的位置
      for (let i = 0; i < currentLayers.length; i++) {
        if (!prevLayers.find(l => l.id === currentLayers[i].id)) {
          return Math.max(0, i - 1); // 返回原图层的索引
        }
      }
      return 0;
    },
    
    // 检查图层顺序变化
    checkLayerOrderChange(prevLayers, currentLayers) {
      const result = {
        changed: false,
        type: '',
        index: -1
      };
      
      // 如果图层数量不同，不考虑顺序变化
      if (prevLayers.length !== currentLayers.length) {
        return result;
      }
      
      // 查找第一个不匹配的位置
      for (let i = 0; i < prevLayers.length; i++) {
        if (prevLayers[i].id !== currentLayers[i].id) {
          // 确定是向上移动还是向下移动
          const currentIndex = currentLayers.findIndex(l => l.id === prevLayers[i].id);
          if (currentIndex < i) {
            result.changed = true;
            result.type = 'moveLayerUp';
            result.index = i;
          } else {
            result.changed = true;
            result.type = 'moveLayerDown';
            result.index = i;
          }
          break;
        }
      }
      
      return result;
    },
    
    // 查找被修改的图层索引
    findModifiedLayerIndex(prevLayers, currentLayers) {
      // 比较每个图层的属性，找出发生变化的图层
      for (let i = 0; i < prevLayers.length; i++) {
        const prevLayer = prevLayers[i];
        const currentLayer = currentLayers[i];
        
        if (JSON.stringify(prevLayer) !== JSON.stringify(currentLayer)) {
          return i;
        }
      }
      return 0; // 默认返回第一个图层
    },
    
    // 执行操作
    async executeOperation(operation) {
      console.log('执行操作:', operation.type, operation.params);
      
      try {
        switch (operation.type) {
          case 'invertLayer':
            if (this.activeLayerIndex !== operation.params.layerIndex) {
              this.setActiveLayer(operation.params.layerIndex);
            }
            await this.invertLayer();
            break;
            
          case 'updateLayerOpacity':
            if (operation.params.layerIndex >= 0 && operation.params.layerIndex < this.layers.length) {
              this.layers[operation.params.layerIndex].opacity = operation.params.opacity;
              await this.updateLayerOpacity(operation.params.layerIndex);
            }
            break;
            
          case 'moveLayerUp':
            await this.moveLayerUp(operation.params.layerIndex);
            break;
            
          case 'moveLayerDown':
            await this.moveLayerDown(operation.params.layerIndex);
            break;
            
          case 'updateBlendMode':
            if (this.activeLayerIndex !== operation.params.layerIndex) {
              this.setActiveLayer(operation.params.layerIndex);
            }
            await this.updateBlendMode({ target: { value: operation.params.blendMode } });
            break;
            
          case 'updateFilters':
            if (this.activeLayerIndex !== operation.params.layerIndex) {
              this.setActiveLayer(operation.params.layerIndex);
            }
            this.activeFilters = { ...operation.params.filters };
            await this.updateFilters();
            break;
            
          case 'duplicateLayer':
            await this.duplicateLayer(operation.params.layerIndex);
            break;
            
          case 'deleteLayer':
            await this.deleteLayer(operation.params.layerIndex);
            break;
            
          case 'moveLayerPosition': {
            // eslint-disable-next-line no-case-declarations
            if (this.activeLayerIndex !== operation.params.layerIndex) {
              this.setActiveLayer(operation.params.layerIndex);
            }
            // eslint-disable-next-line no-case-declarations
            const layer = this.layers[operation.params.layerIndex];
            if (layer) {
              layer.offsetX = operation.params.newOffsetX;
              layer.offsetY = operation.params.newOffsetY;
              this.redrawCanvas();
            }
            break;
          }
            
          case 'toggleLayerVisibility':
            await this.toggleLayerVisibility(operation.params.layerIndex);
            break;
            
          case 'mergeDown': {
            if (operation.params.upperLayerIndex >= 0 && 
                operation.params.upperLayerIndex < this.layers.length - 1) {
              // 先设置正确的活动图层
              this.setActiveLayer(operation.params.upperLayerIndex);
              // 确保下层图层存在
              if (this.layers[operation.params.upperLayerIndex + 1]) {
                await this.mergeDown();
              }
            }
            break;
          }
            
          case 'renameLayer': {
            if (this.activeLayerIndex !== operation.params.layerIndex) {
              this.setActiveLayer(operation.params.layerIndex);
            }
            const layer = this.layers[operation.params.layerIndex];
            if (layer) {
              layer.name = operation.params.newName;
            }
            break;
          }
            
          default:
            console.warn('未知的操作类型:', operation.type);
        }
      } catch (error) {
        console.error('执行操作时出错:', error);
        throw error;
      }
    },
    
    // 旧版方式执行动作（向后兼容）
    async playLegacyAction(actions) {
      if (!actions || !actions.length) return;
      
      // 保存当前状态用于撤销
      this.saveCanvasState();
      
      // 按顺序执行每个动作
      for (const action of actions) {
        // 执行动作
        await this.executeAction(action);
        
        // 添加小延迟以便观察动作执行过程
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    },
    
    // 执行单个动作（向后兼容，用于旧版动作格式）
    async executeAction(action) {
      switch (action.type) {
        case 'invertLayer':
          if (this.activeLayerIndex !== action.params.layerIndex) {
            this.setActiveLayer(action.params.layerIndex);
          }
          await this.invertLayer();
          break;
          
        case 'updateLayerOpacity': {
          const layer = this.layers[action.params.layerIndex];
          if (layer) {
            layer.opacity = action.params.opacity;
            await this.updateLayerOpacity(action.params.layerIndex);
          }
          break;
        }
          
        case 'moveLayerUp':
          await this.moveLayerUp(action.params.layerIndex);
          break;
          
        case 'moveLayerDown':
          await this.moveLayerDown(action.params.layerIndex);
          break;
          
        case 'updateBlendMode':
          if (this.activeLayerIndex !== action.params.layerIndex) {
            this.setActiveLayer(action.params.layerIndex);
          }
          this.blendMode = action.params.blendMode;
          await this.updateBlendMode({ target: { value: action.params.blendMode } });
          break;
          
        case 'updateFilters':
          if (this.activeLayerIndex !== action.params.layerIndex) {
            this.setActiveLayer(action.params.layerIndex);
          }
          this.activeFilters = { ...action.params.filters };
          await this.updateFilters();
          break;
        
        case 'duplicateLayer':
          await this.duplicateLayer(action.params.layerIndex);
          break;
          
        case 'renameLayer': {
          if (this.activeLayerIndex !== action.params.layerIndex) {
            this.setActiveLayer(action.params.layerIndex);
          }
          const layer = this.layers[this.activeLayerIndex];
          layer.name = action.params.newName;
          break;
        }
          
        case 'moveLayerPosition': {
          if (this.activeLayerIndex !== action.params.layerIndex) {
            this.setActiveLayer(action.params.layerIndex);
          }
          const layer = this.layers[action.params.layerIndex];
          if (layer) {
            layer.offsetX = action.params.newOffsetX;
            layer.offsetY = action.params.newOffsetY;
            this.redrawCanvas();
          }
          break;
        }
          
        case 'mergeDown': {
          if (action.params.upperLayerIndex >= 0 && 
              action.params.upperLayerIndex < this.layers.length - 1) {
            // 先设置正确的活动图层
            this.setActiveLayer(action.params.upperLayerIndex);
            // 确保下层图层存在
            if (this.layers[action.params.upperLayerIndex + 1]) {
              await this.mergeDown();
            }
          }
          break;
        }
          
        default:
          console.warn('未知的动作类型:', action.type);
      }
    },
    
    // 记录动作（旧方式，向后兼容）
    recordAction(actionType, params) {
      if (!this.isRecording) return;
      
      // 创建一个动作对象
      const action = {
        type: actionType,
        params: params,
        timestamp: Date.now()
      };
      
      // 确保记录数组存在
      if (!this.recordedActions) {
        this.recordedActions = [];
      }
      
      // 添加到记录
      this.recordedActions.push(action);
    },
    
    async deleteAction(index) {
      if (this.isPlayingAction) return;
      
      const action = this.loadedActions[index];
      if (!action || !action.file) {
        alert('无效的动作文件');
        return;
      }

      if (!confirm(`确定要删除动作"${action.name}"吗？此操作不可恢复。`)) {
        return;
      }

      try {
        const response = await api.uploadAction(null, action.file);
        if (response.success) {
          // 从列表中移除该动作
          this.loadedActions.splice(index, 1);
          alert('动作删除成功');
        } else {
          alert('删除失败: ' + response.message);
        }
      } catch (error) {
        console.error('删除动作文件出错:', error);
        if (error.response && error.response.data) {
          alert('删除失败: ' + error.response.data.message);
        } else {
          alert('删除失败，请稍后重试');
        }
      }
    },
    
    // 打开选区调整选项
    openSelectionOptions() {
      if (!this.hasSelection) return;
      
      // 显示选项面板
      this.showSelectionOptions = true;
      
      // 重置调整选项
      this.selectionOptions.sizeAdjust = 0;
      this.selectionOptions.smoothness = 1.5;
      
      // 保存原始选区数据以便取消
      if (this.selectionData) {
        this.originalSelectionData = new Uint8Array(this.selectionData);
      }
    },
    
    // 预览选区调整
    previewSelectionAdjust() {
      if (!this.hasSelection || !this.originalSelectionData) return;
      
      const layer = this.layers[this.selectedLayerIndex];
      const layerCanvas = this.layerCanvases[layer.id];
      
      if (!layerCanvas) return;
      
      // 获取画布尺寸
      const width = layerCanvas.width;
      const height = layerCanvas.height;
      
      // 创建临时选区数据
      const tempSelectionData = new Uint8Array(this.originalSelectionData);
      
      // 如果是扩大或缩小选区
      if (this.selectionOptions.sizeAdjust !== 0) {
        // 创建临时画布来实现选区调整
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = width;
        tempCanvas.height = height;
        const tempCtx = tempCanvas.getContext('2d');
        
        // 创建基于选区的掩码图像
        const maskImageData = new ImageData(width, height);
        const maskData = maskImageData.data;
        
        // 将选区数据转为掩码图像
        for (let i = 0; i < tempSelectionData.length; i++) {
          const idx = i * 4;
          if (tempSelectionData[i]) {
            maskData[idx] = 255;
            maskData[idx + 1] = 255;
            maskData[idx + 2] = 255;
            maskData[idx + 3] = 255;
          }
        }
        
        // 将掩码图像绘制到临时画布
        tempCtx.putImageData(maskImageData, 0, 0);
        
        // 应用膨胀或腐蚀滤镜（使用高斯模糊+阈值方法实现平滑边缘）
        const amount = Math.abs(this.selectionOptions.sizeAdjust);
        
        // 先使用高斯模糊平滑边缘
        tempCtx.filter = `blur(${this.selectionOptions.smoothness}px)`;
        tempCtx.drawImage(tempCanvas, 0, 0);
        tempCtx.filter = 'none';
        
        // 创建一个新画布用于扩大/缩小操作
        const adjustCanvas = document.createElement('canvas');
        adjustCanvas.width = width;
        adjustCanvas.height = height;
        const adjustCtx = adjustCanvas.getContext('2d');
        
        // 复制平滑后的选区
        adjustCtx.drawImage(tempCanvas, 0, 0);
        
        if (this.selectionOptions.sizeAdjust > 0) {
          // 扩大选区 - 使用膨胀操作
          adjustCtx.filter = `blur(${amount}px)`;
          adjustCtx.drawImage(adjustCanvas, 0, 0);
          adjustCtx.filter = 'none';
          
          // 调整阈值以保持边缘锐利度
          const dilateData = adjustCtx.getImageData(0, 0, width, height);
          const dilatePixels = dilateData.data;
          
          for (let i = 0; i < dilatePixels.length; i += 4) {
            // 应用阈值使边缘更加明确
            dilatePixels[i + 3] = dilatePixels[i + 3] > 20 ? 255 : 0;
          }
          
          adjustCtx.putImageData(dilateData, 0, 0);
        } else {
          // 缩小选区 - 使用腐蚀操作
          adjustCtx.filter = `blur(${amount}px)`;
          adjustCtx.globalCompositeOperation = 'copy';
          adjustCtx.drawImage(adjustCanvas, 0, 0);
          adjustCtx.filter = 'none';
          adjustCtx.globalCompositeOperation = 'source-over';
          
          // 调整阈值以保持边缘锐利度
          const erodeData = adjustCtx.getImageData(0, 0, width, height);
          const erodePixels = erodeData.data;
          
          for (let i = 0; i < erodePixels.length; i += 4) {
            // 应用更高阈值使腐蚀效果更明显
            erodePixels[i + 3] = erodePixels[i + 3] > 200 ? 255 : 0;
          }
          
          adjustCtx.putImageData(erodeData, 0, 0);
        }
        
        // 将处理后的图像数据读取回选区数据
        const processedImageData = adjustCtx.getImageData(0, 0, width, height);
        const processedData = processedImageData.data;
        
        // 重新生成选区数据
        this.selectionData = new Uint8Array(width * height);
        for (let i = 0; i < processedData.length; i += 4) {
          const idx = i / 4;
          // 使用二值化确保选区边界清晰
          this.selectionData[idx] = processedData[i + 3] > 127 ? 1 : 0;
        }
        
        // 显示更新后的选区
        this.showSelection();
      }
    },
    
    // 应用选区调整
    applySelectionAdjust() {
      // 保存当前状态
      this.saveCanvasState();
      
      // 捕获状态快照
      if (this.isRecording) {
        const adjustmentInfo = `调整选区: 大小 ${this.selectionOptions.sizeAdjust > 0 ? '+' : ''}${this.selectionOptions.sizeAdjust}px, 平滑度 ${this.selectionOptions.smoothness}`;
        this.captureSnapshot(adjustmentInfo);
      }

      // 关闭选项面板
      this.showSelectionOptions = false;
      
      // 清除原始选区数据
      this.originalSelectionData = null;
    },
    
    // 取消选区调整
    cancelSelectionAdjust() {
      // 恢复原始选区数据
      if (this.originalSelectionData) {
        this.selectionData = new Uint8Array(this.originalSelectionData);
      }
      
      // 重新显示原始选区
      this.showSelection();
      
      // 关闭选项面板
      this.showSelectionOptions = false;
      
      // 清除原始选区数据
      this.originalSelectionData = null;
    },
    
    // 添加积分兑换处理方法
    handleExchange() {
      // TODO: 实现积分兑换逻辑
      console.log('点击了积分兑换按钮');
    },
    showExchangeModal() {
      // 检查是否已登录
      if (!localStorage.getItem('token')) {
        alert('请先登录');
        this.router.push('/login');
        return;
      }
      
      this.showModal = true;
      this.cardKey = ''; // 清空输入框
    },
    closeModal() {
      this.showModal = false;
    },
    async handleSubmitExchange() {
      if (!this.cardKey.trim()) {
        alert('请输入卡密');
        return;
      }
      
      try {
        const response = await api.exchangeCardKey({ card_key: this.cardKey.trim() });
        
        if (response.success) {
          // 卡密兑换成功
          alert(`兑换成功！您获得了 ${response.data.points} 积分`);
          
          // 更新用户信息
          await this.fetchUserInfo();
          
          this.closeModal();
        } else {
          // 卡密兑换失败
          alert('兑换失败: ' + response.message);
        }
      } catch (error) {
        console.error('卡密兑换错误:', error);
        alert('网络错误，请稍后重试');
      }
    },
    
    // 获取用户信息
    async fetchUserInfo() {
      if (!localStorage.getItem('token')) {
        this.userInfoError = "请先登录";
        return;
      }
      
      this.loadingUserInfo = true;
      this.userInfoError = null;
      
      try {
        console.log('正在获取用户信息...');
        const response = await api.getUserInfo();
        
        if (response.success) {
          console.log('获取用户信息成功:', response.data);
          this.userInfo = response.data;
          // 更新本地存储的用户信息
          localStorage.setItem('user', JSON.stringify(response.data));
        } else {
          console.error('获取用户信息失败:', response.message);
          this.userInfoError = response.message || '获取用户信息失败';
        }
      } catch (error) {
        console.error('获取用户信息错误:', error);
        if (error.response && error.response.data) {
          this.userInfoError = error.response.data.message;
        } else {
          this.userInfoError = '网络错误，请稍后重试';
        }
      } finally {
        this.loadingUserInfo = false;
      }
    },
    async refreshActionList() {
      try {
        const response = await api.uploadAction(); // 发送GET请求获取动作文件列表
        if (response.success) {
          // 直接使用返回的动作数据
          this.loadedActions = response.data;
          if (this.loadedActions.length === 0) {
            alert('没有找到可用的动作文件');
          } else {
            alert(`刷新成功，共加载 ${this.loadedActions.length} 个动作`);
          }
        } else {
          console.warn('刷新动作列表失败:', response.message);
          alert('刷新失败: ' + response.message);
        }
      } catch (error) {
        // 如果错误中包含响应数据，尝试使用它
        if (error.response && error.response.data && error.response.data.success) {
          this.loadedActions = error.response.data.data;
          alert('刷新成功，但响应较慢，请检查网络连接');
          return;
        }
        
        // 其他错误情况
        console.error('刷新动作列表出错:', error);
        if (error.code === 'ECONNABORTED') {
          alert('刷新超时，请检查网络连接');
        } else {
          alert('刷新失败，请稍后重试');
        }
      }
    },

    // 打开编辑模态框
    openEditModal(index) {
      const action = this.loadedActions[index];
      if (!action) {
        console.error('无效的动作索引:', index);
        return;
      }

      console.log('正在编辑动作:', action);
      this.editingAction = {
        name: action.name || '',
        required_images: 1,
        required_credits: 0,
        description: '',
        status: 1
      };
      this.showEditModal = true;
    },

    // 关闭编辑模态框
    closeEditModal() {
      this.showEditModal = false;
      this.editingAction = {
        name: '',
        required_images: 1,
        required_credits: 0,
        description: '',
        status: 1
      };
    },

    // 保存动作信息
    async saveAction() {
      if (!this.editingAction.name) {
        alert('请输入动作名称');
        return;
      }

      if (this.editingAction.required_images < 1) {
        alert('所需图片数量必须大于0');
        return;
      }

      try {
        console.log('正在保存动作信息:', this.editingAction);
        const response = await api.updateAction(this.editingAction);
        console.log('保存响应:', response);

        if (response.success) {
          alert('保存成功');
          this.closeEditModal();
          // 刷新动作列表
          await this.refreshActionList();
        } else {
          alert('保存失败: ' + (response.message || '未知错误'));
          if (response.debug_info) {
            console.error('错误详情:', response.debug_info);
          }
        }
      } catch (error) {
        console.error('保存动作信息出错:', error);
        if (error.response) {
          console.error('错误响应:', error.response.data);
          alert('保存失败: ' + (error.response.data.message || '服务器错误'));
        } else if (error.request) {
          alert('保存失败: 无法连接到服务器');
        } else {
          alert('保存失败: ' + error.message);
        }
      }
    },
    
    // 预览动作效果
    async previewAction(index) {
      const action = this.loadedActions[index];
      if (!action) return;
      
      try {
        // 这里应该调用后端API获取示例图片
        const response = await api.getActionPreview(action.id);
        if (response.success) {
          this.previewImageUrl = response.data.preview_url;
          this.currentPreviewAction = action;
          this.previewModalVisible = true;
        } else {
          alert('获取示例图片失败: ' + response.message);
        }
      } catch (error) {
        console.error('预览动作失败:', error);
        alert('预览失败，请稍后重试');
      }
    },

    async fetchActions() {
      console.log('开始获取动作列表...');
      this.loadingActions = true;
      this.actionError = null;
      
      try {
        const response = await api.getActions();
        console.log('动作列表响应:', response);
        
        if (response.success) {
          this.loadedActions = response.data;
          console.log('成功获取动作列表:', this.loadedActions);
        } else {
          throw new Error(response.message || '获取动作列表失败');
        }
      } catch (error) {
        console.error('获取动作列表出错:', error);
        this.actionError = error.message || '获取动作列表失败，请稍后重试';
      } finally {
        this.loadingActions = false;
      }
    },
    
   // 执行用户动作
async executeUserAction(action) {
  if (!action) return;
  
  // 检查条件
  if (this.userInfo.points < action.required_credits) {
    alert('积分不足');
    return;
  }
  
  if (this.layers.length < action.required_images) {
    alert(`此动作需要至少${action.required_images}张图片，请先上传图片`);
    return;
  }
  
  if (!confirm(`确定要使用 ${action.required_credits} 积分执行"${action.name}"吗？`)) {
    return;
  }
  
  this.isPlayingAction = true;
  this.currentActionIndex = action.id;
  
  try {
    // 保存当前状态用于撤销
    this.saveCanvasState();
    
    // 获取管理员动作列表中匹配的动作
    let adminActions = [];
    try {
      console.log('获取管理员动作列表...');
      const response = await api.uploadAction();
      if (response.success) {
        adminActions = response.data;
        console.log('获取到的管理员动作列表:', adminActions);
      }
    } catch (error) {
      console.error('获取管理员动作列表失败:', error);
      throw new Error('获取动作文件失败');
    }
    
    // 在管理员动作列表中查找匹配的动作
    const matchedAction = adminActions.find(a => a.name === action.name);
    if (!matchedAction) {
      throw new Error(`找不到动作"${action.name}"的执行文件`);
    }
    
    console.log('找到匹配的动作文件:', matchedAction);
    
    // 执行动作
    if (matchedAction.version === '2.0' && matchedAction.snapshots) {
      // 新版格式：基于快照的动作执行
      await this.playActionFromSnapshots(matchedAction.snapshots);
    } else if (matchedAction.version === '1.0' && matchedAction.actions) {
      // 旧版格式：基于操作序列的动作执行
      await this.playLegacyAction(matchedAction.actions);
    } else {
      throw new Error('不支持的动作格式');
    }
    
    // 扣除积分
    await this.deductPoints(action.required_credits);
    
    alert('动作执行成功');
  } catch (error) {
    console.error('执行动作失败:', error);
    alert('执行失败: ' + error.message);
  } finally {
    this.isPlayingAction = false;
    this.currentActionIndex = -1;
  }
},
    
    // 扣除积分
    async deductPoints(points) {
      try {
        const response = await api.deductPoints({ points });
        if (response.success) {
          // 更新用户信息
          await this.fetchUserInfo();
        } else {
          throw new Error(response.message);
        }
      } catch (error) {
        console.error('扣除积分失败:', error);
        throw new Error('扣除积分失败，请稍后重试');
      }
    },
  }
}
</script>

<style scoped>
.home-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 550px;
  margin: 0 auto;
  padding: 0.8rem;
  padding-bottom: 70px; /* 为底部导航栏留出空间 */
  overflow-y: auto;
  height: 100vh;
  gap: 0.8rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
}

/* 模块卡片样式 */
.module-card {
  width: 100%;
  border-radius: 12px;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  margin-bottom: 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.8);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.module-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
}

.module-title {
  margin-top: 0;
  margin-bottom: 0.8rem;
  font-size: 1.1rem;
  color: #333;
  padding-bottom: 0.6rem;
  text-align: center;
  font-weight: 600;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  position: relative;
}

.module-title::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(to right, #2196F3, #4CAF50);
  border-radius: 3px;
}

/* 移除之前的顶部色条样式 */
.action-module,
.upload-module,
.canvas-module,
.edit-actions-module,
.layers-module,
.blend-module,
.filter-module,
.drawing-module {
  border-top: none;
}

/* 为不同模块添加不同的底部指示条 */
.action-module .module-title::after {
  background: linear-gradient(to right, #9c27b0, #e040fb);
}

.upload-module .module-title::after {
  background: linear-gradient(to right, #4CAF50, #8BC34A);
}

.canvas-module .module-title::after {
  background: linear-gradient(to right, #2196F3, #03A9F4);
}

.edit-actions-module .module-title::after {
  background: linear-gradient(to right, #ff9800, #FFC107);
}

.layers-module .module-title::after {
  background: linear-gradient(to right, #795548, #A1887F);
}

.blend-module .module-title::after {
  background: linear-gradient(to right, #607d8b, #90A4AE);
}

.filter-module .module-title::after {
  background: linear-gradient(to right, #f44336, #FF5722);
}

.drawing-module .module-title::after {
  background: linear-gradient(to right, #009688, #4DB6AC);
}

.upload-section {
  width: 100%;
  text-align: center;
}

.upload-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 0.5rem;
}

.preset-action-wrapper {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  width: 100%;
}

.preset-select {
  flex: 1;
  padding: 0.8rem;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background-color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.8rem center;
  background-size: 1rem;
  padding-right: 2.5rem;
}

.preset-select:focus {
  outline: none;
  border-color: rgba(156, 39, 176, 0.5);
  box-shadow: 0 2px 12px rgba(156, 39, 176, 0.1);
}

.execute-btn {
  background: linear-gradient(135deg, #9c27b0, #e040fb);
  color: white;
  padding: 0.7rem 1.2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 4px 10px rgba(156, 39, 176, 0.2);
}

.execute-btn:hover {
  background: linear-gradient(135deg, #8e24aa, #d500f9);
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(156, 39, 176, 0.3);
}

.execute-btn:disabled {
  background: linear-gradient(135deg, #e0e0e0, #eeeeee);
  cursor: not-allowed;
  color: #9e9e9e;
  box-shadow: none;
  transform: none;
}

.upload-btn {
  background: linear-gradient(135deg, #4CAF50, #8BC34A);
  color: white;
  padding: 0.8rem 1.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  margin-bottom: 0.8rem;
  box-shadow: 0 4px 10px rgba(76, 175, 80, 0.2);
  font-weight: 500;
}

.upload-btn:hover {
  background: linear-gradient(135deg, #43a047, #7cb342);
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(76, 175, 80, 0.3);
}

.upload-icon {
  margin-right: 0.5rem;
  font-size: 1.2rem;
  font-weight: bold;
}

.file-input {
  display: none;
}

/* 信息标签样式 */
.image-info {
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #555;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.image-info p {
  margin: 0;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.info-label {
  color: #777;
  font-weight: 500;
}

.info-value {
  color: #333;
  font-weight: 600;
}

/* 画布区域 */
.canvas-section {
  width: 100%;
}

/* 画布容器样式 */
.canvas-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 1rem;
}

.canvas-container {
  background-color: #f0f0f0;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  width: 600px;
  height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab; /* 鼠标指针显示为可拖动状态 */
}

.canvas-container:active {
  cursor: grabbing; /* 鼠标按下时显示为拖动中状态 */
}

.main-canvas {
  display: block;
  width: 600px;
  height: 600px;
  background-color: transparent;
}

.canvas-actions {
  display: flex;
  justify-content: center;
  gap: 0.6rem;
  margin-bottom: 0.8rem;
}

.action-btn {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #ff9800, #FFC107);
  color: white;
  padding: 0.7rem 1.2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 4px 10px rgba(255, 152, 0, 0.2);
}

.action-btn:hover {
  background: linear-gradient(135deg, #f57c00, #ffb300);
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(255, 152, 0, 0.3);
}

.action-btn:disabled {
  background: linear-gradient(135deg, #e0e0e0, #eeeeee);
  cursor: not-allowed;
  color: #9e9e9e;
  box-shadow: none;
  transform: none;
}

.icon {
  margin-right: 0.25rem;
}

/* 图层管理区域 */
.layers-section {
  width: 100%;
  margin-bottom: 1.5rem;
  background-color: #f8f8f8;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.layers-section h3 {
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  color: #333;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
}

.layers-list {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 0.8rem;
  max-height: 250px;
  overflow-y: auto;
  padding: 0.2rem;
}

.layer-item {
  display: flex;
  align-items: center;
  padding: 0.8rem;
  background-color: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
}

.layer-item:hover {
  background-color: rgba(245, 245, 245, 0.8);
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.active-layer {
  border-color: rgba(121, 85, 72, 0.3);
  background: linear-gradient(to right, rgba(121, 85, 72, 0.05), rgba(169, 136, 127, 0.05));
  box-shadow: 0 4px 10px rgba(121, 85, 72, 0.1);
}

.layer-preview {
  width: 50px;
  height: 50px;
  margin-right: 0.8rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.layer-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.layer-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.layer-name {
  font-weight: 500;
  color: #333;
}

.layer-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.layer-opacity {
  min-width: 45px;
  font-size: 0.9rem;
  color: #555;
}

.layer-actions {
  display: flex;
  gap: 0.5rem;
}

.visibility-btn, 
.delete-btn,
.order-btn,
.duplicate-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.3rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  background-color: rgba(245, 245, 245, 0.7);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.visibility-btn:hover, 
.delete-btn:hover,
.order-btn:hover,
.duplicate-btn:hover {
  background-color: rgba(224, 224, 224, 0.9);
  transform: translateY(-2px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.06);
}

.delete-btn:hover {
  background-color: rgba(244, 67, 54, 0.1);
}

.duplicate-btn:hover {
  background-color: rgba(33, 150, 243, 0.1);
}

.duplicate-icon {
  font-size: 16px;
  font-weight: bold;
  color: #2196F3;
}

.order-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.order-icon {
  font-size: 14px;
  font-weight: bold;
  color: #555;
}

.layer-add-section {
  margin-top: 0.75rem;
  text-align: center;
}

.add-layer-btn {
  background: linear-gradient(135deg, #795548, #A1887F);
  color: white;
  padding: 0.7rem 1.2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(121, 85, 72, 0.2);
}

.add-layer-btn:hover {
  background: linear-gradient(135deg, #6d4c41, #8d6e63);
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(121, 85, 72, 0.3);
}

.plus-icon {
  margin-right: 0.4rem;
  font-weight: bold;
}

/* 混合模式 */
.blend-mode-selector {
  width: 100%;
}

.blend-select {
  width: 100%;
  padding: 0.8rem;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background-color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.8rem center;
  background-size: 1rem;
  padding-right: 2.5rem;
}

.blend-select:focus {
  outline: none;
  border-color: rgba(156, 39, 176, 0.5);
  box-shadow: 0 2px 12px rgba(156, 39, 176, 0.1);
}

/* 滤镜控制 */
.filter-controls {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.filter-item {
  display: grid;
  grid-template-columns: 80px 1fr 50px;
  align-items: center;
  gap: 0.6rem;
  padding: 0.3rem 0;
}

.filter-item label {
  color: #555;
  font-weight: 500;
}

.filter-item span {
  text-align: right;
  color: #555;
  min-width: 50px;
}

/* 绘图工具 */
.drawing-tools {
  width: 100%;
}

.tool-buttons {
  display: flex;
  gap: 0.6rem;
  margin-bottom: 0.8rem;
}

.tool-btn {
  flex: 1;
  padding: 0.8rem 1rem;
  background-color: rgba(224, 224, 224, 0.7);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.tool-btn:hover {
  background-color: rgba(208, 208, 208, 0.8);
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

.tool-btn.active {
  background: linear-gradient(135deg, #009688, #4DB6AC);
  color: white;
  border-color: transparent;
  box-shadow: 0 4px 10px rgba(0, 150, 136, 0.2);
}

.tool-btn.active:hover {
  background: linear-gradient(135deg, #00897b, #26a69a);
  box-shadow: 0 6px 15px rgba(0, 150, 136, 0.3);
}

.tool-options {
  padding: 0.8rem;
  background-color: rgba(245, 245, 245, 0.7);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border-radius: 8px;
  margin-top: 0.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
}

.tool-options label {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #333;
}

.tool-options input {
  flex: 1;
}

/* 移动端优化 */
@media (max-width: 480px) {
  .home-container {
    padding: 0.75rem;
  }
  
  .filter-item {
    grid-template-columns: 70px 1fr 40px;
    font-size: 0.9rem;
  }
  
  .action-btn {
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
  }
}

/* 滑块样式 */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 10px;
  background: linear-gradient(to right, #2196F3, #90CAF9);
  outline: none;
  transition: all 0.3s ease;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(33, 150, 243, 0.4);
  transition: all 0.3s ease;
  border: 2px solid #2196F3;
}

input[type="range"]::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(33, 150, 243, 0.4);
  transition: all 0.3s ease;
  border: 2px solid #2196F3;
}

input[type="range"]:hover::-webkit-slider-thumb {
  transform: scale(1.1);
  box-shadow: 0 3px 8px rgba(33, 150, 243, 0.6);
}

input[type="range"]:hover::-moz-range-thumb {
  transform: scale(1.1);
  box-shadow: 0 3px 8px rgba(33, 150, 243, 0.6);
}

/* 滚动条美化 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  transition: all 0.3s ease;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.2);
}

.action-icon {
  width: 20px;
  height: 20px;
  display: block;
}

.layer-tools {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* 图层工具按钮容器 */
.layer-tool-buttons {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.8rem;
  justify-content: center;
  padding: 0.5rem;
}

/* 图层工具按钮样式 */
.layer-tool-btn {
  background: linear-gradient(135deg, #795548, #A1887F);
  color: white;
  padding: 0.7rem 0.8rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(121, 85, 72, 0.2);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 100px;
  max-width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.layer-tool-btn:hover {
  background: linear-gradient(135deg, #6d4c41, #8d6e63);
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(121, 85, 72, 0.3);
}

.layer-tool-btn:disabled {
  background: linear-gradient(135deg, #e0e0e0, #eeeeee);
  cursor: not-allowed;
  color: #9e9e9e;
  box-shadow: none;
  transform: none;
}

/* 响应式调整 */
@media (max-width: 480px) {
  .layer-tool-buttons {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.6rem;
  }
  
  .layer-tool-btn {
    font-size: 0.85rem;
    padding: 0.6rem 0.6rem;
  }
}

.stroke-options {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.stroke-option {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.stroke-option label {
  color: #555;
  font-weight: 500;
}

.stroke-option input {
  flex: 1;
}

.stroke-actions {
  display: flex;
  justify-content: space-between;
}

.apply-btn,
.cancel-btn {
  background: linear-gradient(135deg, #795548, #A1887F);
  color: white;
  padding: 0.7rem 1.2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(121, 85, 72, 0.2);
}

.apply-btn:hover,
.cancel-btn:hover {
  background: linear-gradient(135deg, #6d4c41, #8d6e63);
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(121, 85, 72, 0.3);
}

.color-picker {
  width: 100%;
  padding: 0.8rem;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background-color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
}

.color-picker:focus {
  outline: none;
  border-color: rgba(156, 39, 176, 0.5);
  box-shadow: 0 2px 12px rgba(156, 39, 176, 0.1);
}

.layer-operations {
  display: flex;
  justify-content: center;
  gap: 0.6rem;
}

/* 填充选项面板样式 */
.fill-options {
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  margin-top: 0.8rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.fill-option {
  margin-bottom: 1rem;
}

.fill-option label {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #333;
}

.fill-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.8rem;
}

/* 临时画布样式 */
.temp-canvas {
  position: absolute;
  pointer-events: none;
  z-index: 1000;
}

/* 缩放控制模块样式 */
.zoom-module {
  margin-bottom: 1.5rem;
}

.zoom-controls {
  margin: 0.5rem 0;
}

.zoom-slider {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.8rem;
}

.zoom-slider input {
  flex: 1;
  margin-right: 1rem;
}

.zoom-buttons {
  display: flex;
  gap: 0.5rem;
}

.zoom-btn {
  flex: 1;
  padding: 0.4rem 0.6rem;
  background-color: #f1f1f1;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.zoom-btn:hover {
  background-color: #e5e5e5;
}

/* 图层移动控制 */
.layer-move-controls {
  margin-top: 1rem;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.move-controls-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr 1fr 1fr;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.move-btn {
  background: linear-gradient(135deg, #795548, #A1887F);
  color: white;
  padding: 0.6rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(121, 85, 72, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.move-btn:hover {
  background: linear-gradient(135deg, #6d4c41, #8d6e63);
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(121, 85, 72, 0.3);
}

.up-btn {
  grid-column: 2;
  grid-row: 1;
}

.left-btn {
  grid-column: 1;
  grid-row: 2;
}

.move-center {
  grid-column: 2;
  grid-row: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 0.5rem;
}

.right-btn {
  grid-column: 3;
  grid-row: 2;
}

.down-btn {
  grid-column: 2;
  grid-row: 3;
}

.direction-icon {
  font-weight: bold;
}

.move-controls-actions {
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
}

.reset-position-btn {
  background: linear-gradient(135deg, #795548, #A1887F);
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(121, 85, 72, 0.2);
  width: 100%;
}

.reset-position-btn:hover {
  background: linear-gradient(135deg, #6d4c41, #8d6e63);
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(121, 85, 72, 0.3);
}

.pixel-info {
  color: #555;
  font-size: 0.9rem;
  font-weight: bold;
}

/* 工具激活状态 */
.active-tool {
  background: linear-gradient(135deg, #2196F3, #64B5F6) !important;
  color: white !important;
  border-color: transparent !important;
  box-shadow: 0 4px 10px rgba(33, 150, 243, 0.3) !important;
}

.active-tool:hover {
  background: linear-gradient(135deg, #1976D2, #42A5F5) !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(33, 150, 243, 0.4) !important;
}

/* 填充图层选项面板样式 */
.fill-layer-options {
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  margin-top: 0.8rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.fill-layer-option {
  margin-bottom: 1rem;
}

.fill-layer-option label {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #333;
}

.fill-layer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.8rem;
}

.fill-type-select {
  width: 100%;
  padding: 0.8rem;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background-color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
}

.fill-type-select:focus {
  outline: none;
  border-color: rgba(156, 39, 176, 0.5);
  box-shadow: 0 2px 12px rgba(156, 39, 176, 0.1);
}

.image-upload {
  width: 100%;
  padding: 0.8rem;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background-color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.fill-image-preview {
  margin-top: 0.8rem;
  border-radius: 8px;
  overflow: hidden;
  max-height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.05);
}

.fill-image-preview img {
  max-width: 100%;
  max-height: 150px;
  object-fit: contain;
}

.web-version-btn {
  background: linear-gradient(135deg, #1890ff, #40a9ff) !important;
  color: white !important;
}

.web-version-btn:hover {
  background: linear-gradient(135deg, #096dd9, #1890ff) !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(24, 144, 255, 0.3);
}

.scale-options {
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  margin-top: 0.8rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.scale-option {
  margin-bottom: 1rem;
}

.scale-option label {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #333;
}

.scale-input {
  width: 80px;
  padding: 0.4rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.scale-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.8rem;
  margin-top: 1rem;
}

.record-btn {
  background: linear-gradient(135deg, #f44336, #ff5252);
  color: white;
  padding: 0.7rem 1.2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 4px 10px rgba(244, 67, 54, 0.2);
  position: relative;
  overflow: hidden;
}

.record-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  background: #fff;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
}

.record-btn.recording {
  background: linear-gradient(135deg, #d32f2f, #ff1744);
  animation: pulse 2s infinite;
}

.record-btn.recording::before {
  opacity: 1;
  animation: blink 1s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 4px 10px rgba(244, 67, 54, 0.2);
  }
  50% {
    box-shadow: 0 4px 20px rgba(244, 67, 54, 0.4);
  }
  100% {
    box-shadow: 0 4px 10px rgba(244, 67, 54, 0.2);
  }
}

@keyframes blink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

.record-btn:hover {
  background: linear-gradient(135deg, #d32f2f, #ff1744);
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(244, 67, 54, 0.3);
}

.manage-btn {
  background: linear-gradient(135deg, #673ab7, #9c27b0);
  color: white;
  padding: 0.7rem 1.2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 4px 10px rgba(103, 58, 183, 0.2);
}

.manage-btn:hover {
  background: linear-gradient(135deg, #5e35b1, #8e24aa);
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(103, 58, 183, 0.3);
}

.manage-btn:disabled {
  background: linear-gradient(135deg, #e0e0e0, #eeeeee);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.action-manager {
  margin-top: 1rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.action-list {
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 1rem;
}

.no-actions {
  text-align: center;
  color: #666;
  padding: 1rem;
}

.action-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem;
  border-bottom: 1px solid #eee;
}

.action-item:last-child {
  border-bottom: none;
}

.action-info {
  flex: 1;
}

.action-name {
  font-weight: 500;
  color: #333;
  margin-right: 1rem;
}

.action-date {
  font-size: 0.9rem;
  color: #666;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.play-btn {
  background: linear-gradient(135deg, #4caf50, #8bc34a);
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.play-btn:hover {
  background: linear-gradient(135deg, #43a047, #7cb342);
  transform: translateY(-1px);
}

.play-btn:disabled {
  background: linear-gradient(135deg, #e0e0e0, #eeeeee);
  cursor: not-allowed;
  transform: none;
}

.delete-btn {
  background: linear-gradient(135deg, #f44336, #e57373);
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.delete-btn:hover {
  background: linear-gradient(135deg, #d32f2f, #ef5350);
  transform: translateY(-1px);
}

.delete-btn:disabled {
  background: linear-gradient(135deg, #e0e0e0, #eeeeee);
  cursor: not-allowed;
  transform: none;
}

.action-manager-footer {
  text-align: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}

.load-btn {
  background: linear-gradient(135deg, #2196f3, #64b5f6);
  color: white;
  padding: 0.7rem 1.2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 4px 10px rgba(33, 150, 243, 0.2);
}

.load-btn:hover {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(33, 150, 243, 0.3);
}

.load-btn:disabled {
  background: linear-gradient(135deg, #e0e0e0, #eeeeee);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.selection-adjust-options {
  background: #f8f8f8;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.selection-option {
  margin-bottom: 16px;
}

.selection-option label {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #555;
  font-size: 14px;
}

.selection-option input[type="range"] {
  width: 100%;
  -webkit-appearance: none;
  appearance: none;
  height: 6px;
  background: linear-gradient(to right, #2196F3, #90CAF9);
  border-radius: 3px;
  outline: none;
}

.selection-option input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  background: white;
  border: 2px solid #2196F3;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(33, 150, 243, 0.4);
}

.selection-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}

/* 动作模块说明文本 */
.module-description {
  font-size: 0.85rem;
  color: #795548;
  margin: 0 0 10px 0;
  padding: 0 10px;
  text-align: center;
  font-style: italic;
}

/* 积分模块样式 */
.points-module {
  width: 100%;
  margin-bottom: 0.7rem;
}

.points-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
}

.points-info {
  display: flex;
  align-items: center;
}

.points-value {
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
}

.points-number {
  font-size: 1.8rem;
  font-weight: 600;
  color: #1296db;
  line-height: 1;
}

.points-label {
  font-size: 0.9rem;
  color: #666;
}

.exchange-btn {
  background: linear-gradient(135deg, #1296db, #64b5f6);
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: 20px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(18, 150, 219, 0.2);
}

.exchange-btn:hover {
  background: linear-gradient(135deg, #0d87c7, #42a5f5);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(18, 150, 219, 0.3);
}

.exchange-btn:active {
  transform: translateY(1px);
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.modal-content {
  width: 85%;
  max-width: 320px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  animation: modalShow 0.3s ease;
}

@keyframes modalShow {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  padding: 0;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #333;
}

.modal-body {
  padding: 1.2rem;
}

.input-group {
  margin-bottom: 1rem;
}

.card-input {
  width: 100%;
  padding: 0.8rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.card-input:focus {
  outline: none;
  border-color: #1296db;
  box-shadow: 0 0 0 2px rgba(18, 150, 219, 0.1);
}

.tip-text {
  text-align: center;
  color: #666;
  font-size: 0.85rem;
  margin-bottom: 1.2rem;
}

.submit-btn {
  width: 100%;
  padding: 0.8rem;
  background: linear-gradient(135deg, #1296db, #64b5f6);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.submit-btn:hover {
  background: linear-gradient(135deg, #0d87c7, #42a5f5);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(18, 150, 219, 0.2);
}

.submit-btn:active {
  transform: translateY(1px);
}

/* 适配小屏幕设备 */
@media screen and (max-width: 320px) {
  .points-number {
    font-size: 1.6rem;
  }
  
  .points-label {
    font-size: 0.8rem;
  }
  
  .exchange-btn {
    font-size: 0.85rem;
    padding: 0.5rem 1rem;
  }
  
  .modal-content {
    width: 90%;
  }
  
  .modal-header h3 {
    font-size: 1rem;
  }
  
  .card-input {
    font-size: 0.9rem;
    padding: 0.7rem;
  }
  
  .submit-btn {
    font-size: 0.9rem;
    padding: 0.7rem;
  }
}

/* 积分模块状态提示文本 */
.loading-text, .error-text {
  text-align: center;
  font-size: 0.8rem;
  margin: 0.2rem 0 0;
  padding: 0;
}

.loading-text {
  color: #1296db;
}

.error-text {
  color: #f44336;
}

.refresh-btn {
  background: linear-gradient(135deg, #2196f3, #64b5f6);
  color: white;
  padding: 0.7rem 1.2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 4px 10px rgba(33, 150, 243, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
}

.refresh-btn:hover {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(33, 150, 243, 0.3);
}

.refresh-btn:disabled {
  background: linear-gradient(135deg, #e0e0e0, #eeeeee);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.refresh-icon {
  font-size: 1.2rem;
  animation: spin 2s linear infinite paused;
}

.refresh-btn:hover .refresh-icon {
  animation-play-state: running;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.edit-btn {
  background: linear-gradient(135deg, #ff9800, #ffc107);
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.edit-btn:hover {
  background: linear-gradient(135deg, #f57c00, #ffb300);
  transform: translateY(-1px);
}

.edit-btn:disabled {
  background: linear-gradient(135deg, #e0e0e0, #eeeeee);
  cursor: not-allowed;
  transform: none;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 500;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 0.8rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: #2196f3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.form-textarea {
  min-height: 100px;
  resize: vertical;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

.save-btn {
  background: linear-gradient(135deg, #4caf50, #8bc34a);
  color: white;
  padding: 0.8rem 1.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.save-btn:hover {
  background: linear-gradient(135deg, #43a047, #7cb342);
  transform: translateY(-1px);
}

.cancel-btn {
  background: linear-gradient(135deg, #9e9e9e, #bdbdbd);
  color: white;
  padding: 0.8rem 1.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background: linear-gradient(135deg, #757575, #9e9e9e);
  transform: translateY(-1px);
}

.user-actions-list {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 0.8rem;
  max-height: 250px;
  overflow-y: auto;
  padding: 0.2rem;
}

.user-action-item {
  display: flex;
  align-items: center;
  padding: 0.8rem;
  background-color: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
}

.user-action-item:hover {
  background-color: rgba(245, 245, 245, 0.8);
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.action-main-info {
  flex: 1;
}

.action-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.action-status {
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.requirement-icon {
  font-size: 1rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.preview-btn {
  background: linear-gradient(135deg, #4caf50, #8bc34a);
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.preview-btn:hover {
  background: linear-gradient(135deg, #43a047, #7cb342);
  transform: translateY(-1px);
}

.preview-btn:disabled {
  background: linear-gradient(135deg, #e0e0e0, #eeeeee);
  cursor: not-allowed;
  transform: none;
}

.execute-action-btn {
  background: linear-gradient(135deg, #ff9800, #ffc107);
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.execute-action-btn:hover {
  background: linear-gradient(135deg, #f57c00, #ffb300);
  transform: translateY(-1px);
}

.execute-action-btn:disabled {
  background: linear-gradient(135deg, #e0e0e0, #eeeeee);
  cursor: not-allowed;
  transform: none;
}

/* 预览模态框样式 */
.preview-modal {
  max-width: 600px;
  width: 90%;
}

.preview-body {
  padding: 0;
}

.preview-image-container {
  width: 100%;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.no-preview {
  color: #666;
  font-size: 0.9rem;
}

.preview-info {
  padding: 1rem;
}

.preview-requirements {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.8rem;
}

.preview-description {
  color: #666;
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.5;
}

/* 状态样式 */
.action-status {
  font-size: 0.8rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  background-color: #f44336;
  color: white;
}

.action-status.available {
  background-color: #4caf50;
}

.disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.disabled:hover {
  transform: none;
  box-shadow: none;
}

.action-requirements {
  margin-top: 0.5rem;
  display: flex;
  gap: 1rem;
  color: #666;
  font-size: 0.9rem;
}

.action-title h4 {
  margin: 0;
  color: #333;
  font-size: 1rem;
}

.loading-actions, .error-message {
  text-align: center;
  padding: 1rem;
  color: #666;
}

.error-message {
  color: #f44336;
}

.action-description {
  margin: 0.5rem 0;
  font-size: 0.9rem;
  color: #666;
  line-height: 1.4;
}
</style>
